import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useUnread } from '../contexts/UnreadContext';
import TeamSelector from '../components/TeamSelector';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  UserGroupIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/outline';

const TeamChat = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { socket, isConnected, emit, on, off } = useSocket();
  const { markAsRead } = useUnread();
  const [messages, setMessages] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showAllTeams, setShowAllTeams] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMessages, setFilteredMessages] = useState([]);
  const validTabs = ['team', 'inbox', 'announcements'];
  const initialTab = validTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'team';
  const [messageTypeFilter, setMessageTypeFilter] = useState('all'); // depends on active tab
  const [activeTab, setActiveTab] = useState(initialTab); // 'team' | 'inbox'
  const messagesEndRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [directMessageRecipient, setDirectMessageRecipient] = useState(null);
  const [messageType, setMessageType] = useState(initialTab === 'inbox' ? 'direct' : 'team'); // 'team' or 'direct'
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelDescription, setNewChannelDescription] = useState('');
  const [channelTeamId, setChannelTeamId] = useState(null);
  const [channelMembers, setChannelMembers] = useState([]);
  const [availableChannelTeams, setAvailableChannelTeams] = useState([]);
  const [availableChannelTeamMembers, setAvailableChannelTeamMembers] = useState([]);
  const [fetchingChannelTeams, setFetchingChannelTeams] = useState(false);
  const [fetchingChannelMembers, setFetchingChannelMembers] = useState(false);
  // Remove local messageReactions state - now using message.reactions directly
  const [showReactionPicker, setShowReactionPicker] = useState(null); // messageId
  const [selectedInboxMessage, setSelectedInboxMessage] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [showInboxDetail, setShowInboxDetail] = useState(false);
  const [selectedTeamThread, setSelectedTeamThread] = useState(null);
  const [showTeamThreadDetail, setShowTeamThreadDetail] = useState(false);
  const [showComposeMessage, setShowComposeMessage] = useState(false);
  const [composeRecipients, setComposeRecipients] = useState([]);
  const [composeSubject, setComposeSubject] = useState('');
  const [availableTeamMembers, setAvailableTeamMembers] = useState([]);
  const [fetchingMembers, setFetchingMembers] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [teamAccessMap, setTeamAccessMap] = useState({});
  const [updatingParentChatSetting, setUpdatingParentChatSetting] = useState(false);

  // Org announcements state
  const [userOrgs, setUserOrgs] = useState([]);
  const [selectedAnnouncementOrgId, setSelectedAnnouncementOrgId] = useState(null);
  const [announcementMessages, setAnnouncementMessages] = useState([]);
  const [newAnnouncementContent, setNewAnnouncementContent] = useState('');
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  const announcementsEndRef = useRef(null);

  // Common emojis for reactions
  const commonEmojis = ['👍', '❤️', '😄', '😢', '😮', '🔥', '💯', '🎉', '👏', '🤔'];

  // Reaction functions
  const handleReaction = async (messageId, emoji) => {
    try {
      const message = messages.find(m => m.id === messageId);
      const currentReactions = message?.reactions || {};
      const userReactions = currentReactions[emoji] || [];
      const hasReacted = userReactions.includes(user?.id);
      
      const action = hasReacted ? 'remove' : 'add';
      
      await axios.post(`/api/messages/${messageId}/reactions`, {
        emoji,
        action
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      // Update the message reactions optimistically
      setMessages(prev => 
        prev.map(msg => {
          if (msg.id === messageId) {
            const updatedReactions = { ...msg.reactions };
            if (action === 'add') {
              updatedReactions[emoji] = [...(updatedReactions[emoji] || []), user.id];
            } else {
              updatedReactions[emoji] = (updatedReactions[emoji] || []).filter(id => id !== user.id);
              if (updatedReactions[emoji].length === 0) {
                delete updatedReactions[emoji];
              }
            }
            return { ...msg, reactions: updatedReactions };
          }
          return msg;
        })
      );

      // Emit socket event for real-time updates
      if (isConnected) {
        emit('message_reaction', {
          messageId,
          emoji,
          action,
          userId: user.id
        });
      }
    } catch (error) {
      console.error('Error updating reaction:', error);
      toast.error('Failed to update reaction');
    }
    
    setShowReactionPicker(null);
  };

  const getUserReaction = (messageId, emoji) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || !message.reactions) return false;
    const emojiReactions = message.reactions[emoji] || [];
    return emojiReactions.some(reactionUser =>
      typeof reactionUser === 'object'
        ? reactionUser.id === user.id
        : reactionUser === user.id
    );
  };

  const relationshipTypeForTeam = (team) => {
    return team?.relationshipType || team?.relationship_type || null;
  };

  const canManageTeamChatSettings = (team) => {
    if (!team) return false;
    const relationshipType = relationshipTypeForTeam(team);
    return ['admin', 'org_admin', 'app_manager'].includes(user?.role) ||
      ['coach', 'assistant_coach'].includes(relationshipType);
  };

  const canParentPostToTeam = (team) => {
    if (!team) return true;
    const allowParentChat = team?.settings?.allowParentChat !== false;
    if (allowParentChat) return true;

    const relationshipType = relationshipTypeForTeam(team);
    const isParentRelationship = ['parent', 'family'].includes(relationshipType);
    const isParentAccount = user?.role === 'parent';

    return !(isParentRelationship || isParentAccount);
  };

  const resolveTeamById = (teamId) => {
    if (!teamId) return null;
    if (selectedTeam?.id === teamId) return selectedTeam;
    return teamAccessMap[teamId] || null;
  };

  const getParentCommentBlockState = (teamId) => {
    const team = resolveTeamById(teamId);
    if (!team) {
      return { blocked: false, known: false, reason: '' };
    }

    if (canParentPostToTeam(team)) {
      return { blocked: false, known: true, reason: '' };
    }

    return {
      blocked: true,
      known: true,
      reason: 'Parent comments are disabled for this team. Only official team updates are allowed.'
    };
  };

  const getThreadTeamId = (thread) => {
    if (!thread) return null;
    if (thread.teamId) return thread.teamId;

    if (thread.channelId) {
      const matchingChannel = channels.find(channel => channel.id === thread.channelId);
      if (matchingChannel?.teamId) {
        return matchingChannel.teamId;
      }
    }

    return thread.messages?.[0]?.teamId || null;
  };

  // Sample channels for demo
  const sampleChannels = [
    {
      id: 1,
      name: 'General',
      description: 'General team discussions',
      teamId: 1,
      createdBy: { id: 1, name: 'Coach Mike', role: 'coach' },
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
      isDefault: true,
      memberCount: 12
    },
    {
      id: 2,
      name: 'Practice Updates',
      description: 'Practice schedules and updates',
      teamId: 1,
      createdBy: { id: 1, name: 'Coach Mike', role: 'coach' },
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      isDefault: false,
      memberCount: 12
    },
    {
      id: 3,
      name: 'Game Strategy',
      description: 'Game planning and strategy discussions',
      teamId: 1,
      createdBy: { id: 1, name: 'Coach Mike', role: 'coach' },
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      isDefault: false,
      memberCount: 8
    },
    {
      id: 4,
      name: 'Parent Updates',
      description: 'Updates for parents and guardians',
      teamId: 1,
      createdBy: { id: 1, name: 'Coach Mike', role: 'coach' },
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      isDefault: false,
      memberCount: 6
    }
  ];

  // Sample messages for demo
  const sampleMessages = [
    {
      id: 1,
      teamId: 1,
      teamName: 'Demo Braves',
      channelId: 1,
      channelName: 'General',
      sender: { id: 1, name: 'Coach Mike', role: 'coach' },
      content: 'Great practice today everyone! Remember to work on your passing drills at home.',
      timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
      tags: ['practice', 'homework', 'passing'],
      read: true,
    },
    {
      id: 2,
      teamId: 1,
      teamName: 'Demo Braves',
      channelId: 1,
      channelName: 'General',
      sender: { id: 2, name: 'Sarah Johnson', role: 'player' },
      content: 'Coach, I have a question about the new formation we practiced.',
      timestamp: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
      tags: ['formation', 'question'],
      read: true,
    },
    {
      id: 3,
      teamId: 1,
      teamName: 'Demo Braves',
      sender: { id: 1, name: 'Coach Mike', role: 'coach' },
      content: 'Sarah, great question! The new formation is designed to give us better coverage in midfield. We\'ll go over it again on Wednesday.',
      timestamp: new Date(Date.now() - 1000 * 60 * 10), // 10 minutes ago
      tags: ['formation', 'midfield', 'practice'],
      read: false,
    },
    {
      id: 4,
      teamId: 1,
      teamName: 'Demo Braves',
      sender: { id: 3, name: 'John Smith', role: 'parent' },
      content: 'Is there a team meeting this weekend?',
      timestamp: new Date(Date.now() - 1000 * 60 * 5), // 5 minutes ago
      tags: ['meeting', 'weekend'],
      read: false,
    },
    {
      id: 5,
      teamId: 2,
      teamName: 'Demo Padres',
      sender: { id: 4, name: 'Coach Sarah', role: 'coach' },
      content: 'Padres practice is moved to 6 PM tomorrow due to field maintenance.',
      timestamp: new Date(Date.now() - 1000 * 60 * 45), // 45 minutes ago
      tags: ['practice', 'schedule', 'field'],
      read: true,
    },
    {
      id: 6,
      teamId: 2,
      teamName: 'Demo Padres',
      sender: { id: 5, name: 'Timmy Johnson', role: 'player' },
      content: 'Thanks Coach! I\'ll be there at 6 PM.',
      timestamp: new Date(Date.now() - 1000 * 60 * 40), // 40 minutes ago
      tags: ['practice', 'confirmation'],
      read: true,
    },
  ];

  // Fetch messages from API
  const fetchMessages = async () => {
    try {
      if (!user || !user.token) {
        setMessages([]);
        return;
      }

      if (activeTab === 'team' && !selectedTeam && !showAllTeams) {
        setMessages([]);
        return;
      }

      setLoading(true);

      const params = new URLSearchParams();

      if (activeTab === 'team') {
        if (selectedTeam && !showAllTeams) {
          params.append('teamId', selectedTeam.id);
        }
        if (selectedChannel) {
          params.append('channelId', selectedChannel.id);
        }
      } else if (selectedTeam && !showAllTeams) {
        params.append('teamId', selectedTeam.id);
      }

      let messageTypeParam = null;
      if (activeTab === 'team') {
        if (messageTypeFilter === 'all') {
          messageTypeParam = 'team,channel';
        } else if (messageTypeFilter === 'team') {
          messageTypeParam = 'team';
        } else if (messageTypeFilter === 'email') {
          messageTypeParam = 'email';
        }
      } else {
        if (messageTypeFilter === 'all') {
          messageTypeParam = 'inbox';
        } else if (messageTypeFilter === 'direct') {
          messageTypeParam = 'direct';
        } else if (messageTypeFilter === 'email') {
          messageTypeParam = 'email';
        }
      }

      if (messageTypeParam) {
        params.append('messageType', messageTypeParam);
      }

      const queryString = params.toString();
      const response = await axios.get(`/api/messages${queryString ? `?${queryString}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      const payload = Array.isArray(response.data) ? response.data : [];
      setMessages(payload.map(msg => ({
        ...msg,
        tags: Array.isArray(msg.tags) ? msg.tags : []
      })));
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch channels from API
  const fetchChannels = async () => {
    try {
      if (activeTab !== 'team') {
        return;
      }

      if (!user || !user.token) {
        setChannels([]);
        return;
      }

      // Fetch channels - if team is selected, fetch for that team, otherwise fetch all accessible channels
      const url = selectedTeam 
        ? `/api/channels?teamId=${selectedTeam.id}`
        : '/api/channels';
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      const channelData = Array.isArray(response.data) ? response.data : [];
      setChannels(channelData);
      
      // Only auto-select channel if a team is selected and we have channels
      if (selectedTeam && channelData.length > 0 && !selectedChannel) {
        setSelectedChannel(channelData[0]);
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      setChannels([]);
    }
  };

  const fetchTeamAccess = async () => {
    if (!user?.token) {
      setTeamAccessMap({});
      return;
    }

    try {
      const response = await axios.get('/api/teams/my-teams', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      const teams = Array.isArray(response.data) ? response.data : [];
      const nextMap = teams.reduce((acc, team) => {
        acc[team.id] = team;
        return acc;
      }, {});

      setTeamAccessMap(nextMap);
    } catch (error) {
      console.error('Error fetching team access metadata:', error);
    }
  };

  const fetchUserOrgs = async () => {
    if (!user?.token) return;
    try {
      const response = await axios.get('/api/organizations/my-organizations', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      // Each item is an OrganizationUser record with nested Organization
      const orgUsers = Array.isArray(response.data) ? response.data : [];
      setUserOrgs(orgUsers);
      if (orgUsers.length > 0 && !selectedAnnouncementOrgId) {
        setSelectedAnnouncementOrgId(orgUsers[0].organization_id);
      }
    } catch (error) {
      console.error('Error fetching orgs:', error);
    }
  };

  const fetchAnnouncements = async (orgId) => {
    if (!user?.token || !orgId) return;
    setAnnouncementLoading(true);
    try {
      const response = await axios.get(`/api/organizations/${orgId}/announcements`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setAnnouncementMessages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncementMessages([]);
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const handleSendAnnouncement = async (e) => {
    e.preventDefault();
    if (!newAnnouncementContent.trim() || !selectedAnnouncementOrgId) return;
    try {
      const response = await axios.post(
        `/api/organizations/${selectedAnnouncementOrgId}/announcements`,
        { content: newAnnouncementContent.trim() },
        { headers: { 'Authorization': `Bearer ${user.token}` } }
      );
      setAnnouncementMessages(prev => [...prev, response.data]);
      setNewAnnouncementContent('');
      setTimeout(() => announcementsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!selectedAnnouncementOrgId) return;
    try {
      await axios.delete(
        `/api/organizations/${selectedAnnouncementOrgId}/announcements/${announcementId}`,
        { headers: { 'Authorization': `Bearer ${user.token}` } }
      );
      setAnnouncementMessages(prev => prev.filter(a => a.id !== announcementId));
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [user, selectedTeam, selectedChannel, messageTypeFilter, activeTab, showAllTeams]);

  useEffect(() => {
    fetchChannels();
  }, [user, selectedTeam, activeTab, showAllTeams]);

  useEffect(() => {
    fetchTeamAccess();
  }, [user?.token]);

  useEffect(() => {
    if (activeTab === 'announcements') {
      fetchUserOrgs();
    }
  }, [activeTab, user?.token]);

  useEffect(() => {
    if (activeTab === 'announcements' && selectedAnnouncementOrgId) {
      fetchAnnouncements(selectedAnnouncementOrgId);
    }
  }, [activeTab, selectedAnnouncementOrgId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    const normalizedTab = validTabs.includes(tabFromUrl) ? tabFromUrl : 'team';

    if (!validTabs.includes(tabFromUrl)) {
      params.set('tab', normalizedTab);
      setSearchParams(params, { replace: true });
    }

    if (normalizedTab !== activeTab) {
      setActiveTab(normalizedTab);
      setMessageTypeFilter('all');
      setSelectedInboxMessage(null);
      setSelectedThread(null);
      setShowInboxDetail(false);

      if (normalizedTab === 'team') {
        setMessageType('team');
        setDirectMessageRecipient(null);
      } else if (normalizedTab === 'inbox') {
        setMessageType('direct');
      }
    }
  }, [location.search, setSearchParams, activeTab]);

  const handleTeamChange = (team) => {
    setSelectedTeam(team);
    if (team?.id) {
      setTeamAccessMap(prev => ({
        ...prev,
        [team.id]: team
      }));
    }
    setShowAllTeams(false);
    setSelectedChannel(null);
  };

  const handleShowAllTeams = () => {
    setShowAllTeams(true);
    setSelectedTeam(null);
    setSelectedChannel(null);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessageTypeFilter('all');
    setSelectedInboxMessage(null);
    setSelectedThread(null);
    setShowInboxDetail(false);

    if (tab === 'team') {
      setMessageType('team');
      setDirectMessageRecipient(null);
    } else if (tab === 'inbox') {
      setMessageType('direct');
    }

    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    setSearchParams(params, { replace: true });
  };

  const handleUpdateAllowParentChat = async (allowParentChat) => {
    if (!selectedTeam || !user?.token || !canManageTeamChatSettings(selectedTeam)) {
      return;
    }

    setUpdatingParentChatSetting(true);
    const teamId = selectedTeam.id;
    const nextSettings = {
      ...(selectedTeam.settings || {}),
      allowParentChat
    };

    try {
      const response = await axios.put(`/api/teams/${teamId}`, {
        settings: nextSettings
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      const savedSettings = response.data?.settings || nextSettings;

      setSelectedTeam(prev => prev && prev.id === teamId
        ? { ...prev, settings: savedSettings }
        : prev
      );

      setTeamAccessMap(prev => ({
        ...prev,
        [teamId]: {
          ...(prev[teamId] || selectedTeam),
          settings: savedSettings
        }
      }));

      toast.success(allowParentChat ? 'Parents can comment again' : 'Parent comments disabled');
    } catch (error) {
      console.error('Error updating parent comment setting:', error);
      toast.error(error.response?.data?.error || 'Failed to update chat setting');
    } finally {
      setUpdatingParentChatSetting(false);
    }
  };

  // Demo team members for direct messaging
  const teamMembers = {
    1: [ // Demo Braves
      { id: 1, name: 'Coach Mike', role: 'coach' },
      { id: 2, name: 'Sarah Johnson', role: 'player' },
      { id: 3, name: 'Alex Rodriguez', role: 'player' },
      { id: 4, name: 'Jordan Smith', role: 'player' },
      { id: 5, name: 'Emma Wilson', role: 'player' },
      { id: 6, name: 'Mike Johnson', role: 'parent' },
      { id: 7, name: 'Maria Rodriguez', role: 'parent' },
    ],
    2: [ // Demo Padres
      { id: 8, name: 'Coach Sarah', role: 'coach' },
      { id: 9, name: 'Timmy Johnson', role: 'player' },
      { id: 10, name: 'Jimmy Smith', role: 'player' },
    ]
  };

  // Fetch team members for compose message
  const fetchTeamMembersForCompose = async () => {
    if (!selectedTeam || !user?.token) {
      setAvailableTeamMembers([]);
      return;
    }

    setFetchingMembers(true);
    try {
      const response = await axios.get(`/api/user-teams/team/${selectedTeam.id}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      const members = response.data
        .map(ut => ({
          id: ut.User?.id || ut.user_id,
          name: ut.User?.name || 'Unknown',
          role: ut.User?.role || ut.relationship_type || 'member',
          relationshipType: ut.relationship_type
        }))
        .filter(m => m.id && m.id !== user?.id); // Exclude current user

      setAvailableTeamMembers(members);
    } catch (error) {
      console.error('Error fetching team members:', error);
      // Fallback to demo data if API fails
      setAvailableTeamMembers(teamMembers[selectedTeam.id]?.filter(m => m.id !== user?.id) || []);
    } finally {
      setFetchingMembers(false);
    }
  };

  // Fetch available teams for channel creation
  const fetchAvailableTeams = async () => {
    if (!user?.token) {
      setAvailableChannelTeams([]);
      return;
    }

    setFetchingChannelTeams(true);
    try {
      const response = await axios.get('/api/teams/my-teams', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      let teams = Array.isArray(response.data) ? [...response.data] : [];
      
      // For org admins, also fetch teams from their organizations
      const hasOrgPrivileges = ['admin', 'org_admin', 'app_manager'].includes(user?.role);
      if (hasOrgPrivileges) {
        try {
          const orgResponse = await axios.get('/api/organizations/my-organizations', {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          });
          
          const orgs = Array.isArray(orgResponse.data) ? orgResponse.data : [];
          for (const org of orgs) {
            try {
              const orgTeamsResponse = await axios.get(`/api/organizations/${org.id}/teams`, {
                headers: {
                  'Authorization': `Bearer ${user.token}`
                }
              });
              const orgTeams = Array.isArray(orgTeamsResponse.data) ? orgTeamsResponse.data : [];
              teams = [...teams, ...orgTeams.filter(t => !teams.find(existing => existing.id === t.id))];
            } catch (err) {
              console.error(`Error fetching teams for org ${org.id}:`, err);
            }
          }
        } catch (err) {
          console.error('Error fetching organizations:', err);
        }
      }
      
      setAvailableChannelTeams(teams);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setAvailableChannelTeams([]);
    } finally {
      setFetchingChannelTeams(false);
    }
  };

  // Fetch team members for channel creation
  const fetchChannelTeamMembers = async (teamId) => {
    if (!teamId || !user?.token) {
      setAvailableChannelTeamMembers([]);
      return;
    }

    setFetchingChannelMembers(true);
    try {
      const response = await axios.get(`/api/user-teams/team/${teamId}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      const members = response.data
        .map(ut => ({
          id: ut.User?.id || ut.user_id,
          name: ut.User?.name || 'Unknown',
          role: ut.User?.role || ut.relationship_type || 'member',
          relationshipType: ut.relationship_type
        }))
        .filter(m => m.id && m.id !== user?.id); // Exclude current user

      setAvailableChannelTeamMembers(members);
    } catch (error) {
      console.error('Error fetching team members:', error);
      setAvailableChannelTeamMembers([]);
    } finally {
      setFetchingChannelMembers(false);
    }
  };

  // Open compose message modal
  const handleOpenCompose = () => {
    setShowComposeMessage(true);
    setComposeRecipients([]);
    setComposeSubject('');
    setNewMessage('');
    if (selectedTeam) {
      fetchTeamMembersForCompose();
    }
  };

  // Add/remove recipient from compose
  const toggleComposeRecipient = (member) => {
    setComposeRecipients(prev => {
      const exists = prev.find(r => r.id === member.id);
      if (exists) {
        return prev.filter(r => r.id !== member.id);
      } else {
        return [...prev, member];
      }
    });
  };

  // Handle sending composed message
  const handleSendComposedMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() && pendingAttachments.length === 0) {
      toast.error('Please enter a message or add an image');
      return;
    }

    if (!selectedTeam) {
      toast.error('Please select a team');
      return;
    }

    if (composeRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    try {
      const recipientIds = composeRecipients.map(r => r.id);
      const messageType = recipientIds.length === 1 ? 'direct' : 'email';
      
      const messageData = {
        content: newMessage.trim() || '',
        teamId: selectedTeam.id,
        messageType: messageType,
        recipientId: messageType === 'direct' ? recipientIds[0] : null,
        emailRecipients: messageType === 'email' ? recipientIds : [],
        emailSubject: messageType === 'email' ? (composeSubject.trim() || 'Team Message') : null,
        tags: [],
        attachments: pendingAttachments
      };

      const response = await axios.post('/api/messages', messageData, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      // Add message locally
      const newMessagePayload = {
        ...response.data,
        tags: Array.isArray(response.data.tags) ? response.data.tags : []
      };
      setMessages(prev => [...prev, newMessagePayload]);
      
      // Emit to socket if connected
      if (isConnected) {
        emit('send_message', response.data);
      }

      // Close modal and clear form
      setShowComposeMessage(false);
      setComposeRecipients([]);
      setComposeSubject('');
      setNewMessage('');
      setPendingAttachments([]);
      
      toast.success('Message sent successfully!');
      
      // Refresh messages
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle opening channel creation modal
  const handleOpenCreateChannel = () => {
    setShowCreateChannel(true);
    setChannelTeamId(null); // Start with no team selected - user must choose
    setChannelMembers([]);
    setNewChannelName('');
    setNewChannelDescription('');
    fetchAvailableTeams();
  };

  // Toggle channel member selection
  const toggleChannelMember = (member) => {
    setChannelMembers(prev => {
      const exists = prev.find(m => m.id === member.id);
      if (exists) {
        return prev.filter(m => m.id !== member.id);
      } else {
        return [...prev, member];
      }
    });
  };

  // Handle team selection change in channel creation
  const handleChannelTeamChange = (teamId) => {
    setChannelTeamId(teamId);
    setChannelMembers([]);
    if (teamId) {
      fetchChannelTeamMembers(teamId);
    } else {
      setAvailableChannelTeamMembers([]);
    }
  };

  const handleCreateChannel = async (e) => {
    e.preventDefault();
    if (!newChannelName.trim()) {
      toast.error('Please enter a channel name');
      return;
    }
    
    if (!channelTeamId) {
      toast.error('Please select a team');
      return;
    }

    try {
      const memberIds = channelMembers.map(m => m.id);
      const isPrivate = channelMembers.length > 0;
      
      const response = await axios.post('/api/channels', {
        name: newChannelName.trim(),
        description: newChannelDescription.trim(),
        teamId: channelTeamId,
        isPrivate: isPrivate,
        memberIds: isPrivate ? memberIds : [] // Only send members if private channel
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      const newChannel = response.data;
      
      // Refresh channels list to ensure we have all channels
      await fetchChannels();
      
      // Select the newly created channel
      setSelectedChannel(newChannel);
      setNewChannelName('');
      setNewChannelDescription('');
      setChannelTeamId(null);
      setChannelMembers([]);
      setShowCreateChannel(false);
      toast.success('Channel created successfully!');
    } catch (error) {
      console.error('Error creating channel:', error);
      toast.error('Failed to create channel: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel);
    setMessageType('team');
    setDirectMessageRecipient(null);
  };

  useEffect(() => {
    // Mark messages as read when user visits chat page
    markAsRead();
    
    // Initialize channels and set default channel for demo fallback
    setChannels(prev => (prev && prev.length > 0 ? prev : sampleChannels));
    setSelectedChannel(prev => prev || (showAllTeams || selectedTeam ? null : sampleChannels[0])); // Set General as default when scoped
    
    // Reaction data is already initialized in state
    
    if (socket && isConnected) {
      on('new_message', handleNewMessage);
      on('message_read', handleMessageRead);
      on('message_reaction', handleMessageReaction);
      
      return () => {
        off('new_message', handleNewMessage);
        off('message_read', handleMessageRead);
        off('message_reaction', handleMessageReaction);
      };
    }
  }, [socket, isConnected, on, off, showAllTeams, selectedTeam]);

  useEffect(() => {
    if (activeTab === 'team') {
      scrollToBottom();
    }
  }, [messages, activeTab]);

  useEffect(() => {
    filterMessages();
  }, [searchQuery, messages, selectedTeam, showAllTeams, selectedChannel, messageTypeFilter, activeTab]);

  useEffect(() => {
    if (activeTab === 'team') {
      const threads = groupTeamMessagesIntoThreads(filteredMessages);
      
      if (threads.length === 0) {
        setSelectedTeamThread(null);
        setShowTeamThreadDetail(false);
        return;
      }

      const isLargeScreen = typeof window !== 'undefined'
        ? window.matchMedia('(min-width: 1024px)').matches
        : false;

      const isCurrentSelectionStillVisible = selectedTeamThread
        ? threads.some(thread => thread.threadKey === selectedTeamThread.threadKey)
        : false;

      if (!isCurrentSelectionStillVisible && threads.length > 0) {
        if (isLargeScreen) {
          const firstThread = threads[0];
          handleSelectTeamThread(firstThread);
        } else {
          setSelectedTeamThread(null);
          setShowTeamThreadDetail(false);
        }
      }
    } else if (activeTab === 'inbox') {
      const threads = groupMessagesIntoThreads(filteredMessages);
      
      if (threads.length === 0) {
        setSelectedThread(null);
        setSelectedInboxMessage(null);
        setShowInboxDetail(false);
        return;
      }

      const isLargeScreen = typeof window !== 'undefined'
        ? window.matchMedia('(min-width: 1024px)').matches
        : false;

      const isCurrentSelectionStillVisible = selectedThread
        ? threads.some(thread => thread.threadKey === selectedThread.threadKey)
        : false;

      if (!isCurrentSelectionStillVisible && threads.length > 0) {
        if (isLargeScreen) {
          const firstThread = threads[0];
          setSelectedThread(firstThread);
          setSelectedInboxMessage(firstThread.lastMessage);
          setShowInboxDetail(true);
        } else {
          setSelectedThread(null);
          setSelectedInboxMessage(null);
          setShowInboxDetail(false);
        }
      }
    }
  }, [filteredMessages, activeTab, selectedThread, selectedTeamThread]);

  useEffect(() => {
    if (activeTab === 'team' && selectedTeamThread) {
      // Update selected team thread if messages changed
      const threads = groupTeamMessagesIntoThreads(filteredMessages);
      const updatedThread = threads.find(t => t.threadKey === selectedTeamThread.threadKey);
      if (updatedThread) {
        setSelectedTeamThread(updatedThread);
      }
    } else if (activeTab === 'inbox' && selectedThread) {
      // Update selected inbox thread if messages changed
      const threads = groupMessagesIntoThreads(filteredMessages);
      const updatedThread = threads.find(t => t.threadKey === selectedThread.threadKey);
      if (updatedThread) {
        setSelectedThread(updatedThread);
        setSelectedInboxMessage(updatedThread.lastMessage);
      }
    }
  }, [messages, filteredMessages, activeTab, selectedThread, selectedTeamThread]);

  useEffect(() => {
    if (activeTab !== 'inbox' || !selectedInboxMessage || selectedInboxMessage.read) {
      return;
    }

    const isLargeScreen = typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 1024px)').matches
      : false;

    if (showInboxDetail || isLargeScreen) {
      markMessageAsRead(selectedInboxMessage.id);
    }
  }, [activeTab, selectedInboxMessage, showInboxDetail]);

  useEffect(() => {
    if (!user?.token) return;

    const visibleUnreadMessages = filteredMessages.filter(msg => (
      !msg.read && msg.sender?.id !== user?.id
    ));

    if (visibleUnreadMessages.length === 0) return;

    visibleUnreadMessages.forEach(msg => {
      markMessageAsRead(msg.id);
    });
  }, [filteredMessages, user?.id, user?.token]);

  useEffect(() => {
    setDirectMessageRecipient(null);
  }, [selectedTeam]);

  const handleNewMessage = (message) => {
    setMessages(prev => [
      ...prev,
      {
        ...message,
        tags: Array.isArray(message.tags) ? message.tags : []
      }
    ]);
  };

  const handleMessageRead = (messageId) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, read: true } : msg
      )
    );
  };

  const handleMessageReaction = (data) => {
    const { messageId, emoji, action, userId } = data;
    
    setMessages(prev => 
      prev.map(msg => {
        if (msg.id === messageId) {
          const updatedReactions = { ...msg.reactions };
          if (action === 'add') {
            updatedReactions[emoji] = [...(updatedReactions[emoji] || []), userId];
          } else {
            updatedReactions[emoji] = (updatedReactions[emoji] || []).filter(id => id !== userId);
            if (updatedReactions[emoji].length === 0) {
              delete updatedReactions[emoji];
            }
          }
          return { ...msg, reactions: updatedReactions };
        }
        return msg;
      })
    );
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const filterMessages = () => {
    let filtered = messages;

    if (activeTab === 'team') {
      if (messageTypeFilter === 'email') {
        filtered = filtered.filter(message => message.messageType === 'email');
      } else {
        filtered = filtered.filter(message =>
          ['team', 'channel'].includes(message.messageType)
        );

        if (messageTypeFilter === 'team') {
          filtered = filtered.filter(message => message.messageType === 'team');
        }
      }

      if (showAllTeams) {
        // include all teams
      } else if (selectedTeam) {
        filtered = filtered.filter(message => message.teamId === selectedTeam.id);
      } else {
        filtered = [];
      }

      if (selectedChannel && !showAllTeams) {
        filtered = filtered.filter(message => message.channelId === selectedChannel.id);
      }
    } else {
      if (messageTypeFilter === 'direct') {
        filtered = filtered.filter(message => message.messageType === 'direct');
      } else if (messageTypeFilter === 'email') {
        filtered = filtered.filter(message => message.messageType === 'email');
      } else {
        filtered = filtered.filter(message =>
          ['direct', 'email'].includes(message.messageType)
        );
      }

      if (!showAllTeams && selectedTeam) {
        filtered = filtered.filter(message => message.teamId === selectedTeam.id);
      }
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(message =>
        message.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        message.sender.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredMessages(filtered);
  };

  const handlePasteImage = async (e) => {
    if (!user?.token) {
      console.log('User not authenticated, cannot upload image');
      return;
    }

    const clipboardData = e.clipboardData || e.originalEvent?.clipboardData;
    if (!clipboardData) {
      console.log('No clipboard data available');
      return;
    }

    const items = clipboardData.items || clipboardData.files;
    if (!items || items.length === 0) {
      console.log('No clipboard items found');
      return;
    }

    console.log('Clipboard items:', Array.from(items).map(item => ({ 
      type: item.type, 
      kind: item.kind,
      name: item.name 
    })));

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemType = item.type || '';
      const itemKind = item.kind || '';

      // Check if it's an image - handle various formats
      const isImage = itemKind === 'file' || 
                     itemType.startsWith('image/') || 
                     itemType.indexOf('image') !== -1;

      if (isImage) {
        e.preventDefault();
        e.stopPropagation();
        
        let file;
        if (item.getAsFile) {
          file = item.getAsFile();
        } else if (item instanceof File) {
          file = item;
        } else {
          console.log('Could not extract file from clipboard item');
          continue;
        }

        if (!file) {
          console.log('File is null or undefined');
          continue;
        }

        // Validate it's actually an image file
        if (!file.type || !file.type.startsWith('image/')) {
          console.log('File is not an image type:', file.type);
          continue;
        }

        console.log('Found image file:', { name: file.name, type: file.type, size: file.size });

        if (file.size > 10 * 1024 * 1024) {
          toast.error('Image is too large. Maximum size is 10MB.');
          continue;
        }

        if (file.size === 0) {
          toast.error('Image file is empty');
          continue;
        }

        try {
          const formData = new FormData();
          formData.append('image', file);

          const response = await axios.post('/api/messages/upload-image', formData, {
            headers: {
              'Authorization': `Bearer ${user.token}`,
              'Content-Type': 'multipart/form-data'
            }
          });

          if (response.data.success) {
            setPendingAttachments(prev => [...prev, response.data.attachment]);
            toast.success('Image added!');
          } else {
            toast.error('Failed to upload image');
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
          toast.error('Failed to upload image: ' + errorMessage);
        }
        break;
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() && pendingAttachments.length === 0) {
      return;
    }

    if (!user?.token) {
      toast.error('You must be logged in to send messages.');
      return;
    }

    if (!selectedTeam) {
      toast.error('Please select a team before sending a message.');
      return;
    }

    if (messageType === 'direct' && !directMessageRecipient) {
      toast.error('Select a recipient for your direct message.');
      return;
    }

    if (messageType === 'team') {
      const blockState = getParentCommentBlockState(selectedTeam.id);
      if (blockState.blocked) {
        toast.error(blockState.reason);
        return;
      }
    }

    try {
      const messageData = {
        content: newMessage.trim() || '',
        teamId: selectedTeam.id,
        channelId: messageType === 'team' ? selectedChannel?.id : null,
        messageType: messageType,
        recipientId: messageType === 'direct' ? directMessageRecipient?.id : null,
        tags: [],
        attachments: pendingAttachments
      };

      const response = await axios.post('/api/messages', messageData, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      // Add message locally
      const newMessagePayload = {
        ...response.data,
        tags: Array.isArray(response.data.tags) ? response.data.tags : []
      };
      setMessages(prev => [...prev, newMessagePayload]);
      
      // Emit to socket if connected
      if (isConnected) {
        emit('send_message', response.data);
      }

      // Clear form
      setNewMessage('');
      setPendingAttachments([]);
      if (messageType === 'direct') {
        setDirectMessageRecipient(null);
      }
      
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
    }
  };


  const handleSelectInboxMessage = (message) => {
    setSelectedInboxMessage(message);
    setShowInboxDetail(true);
  };

  const handleSelectThread = (thread) => {
    setSelectedThread(thread);
    setSelectedInboxMessage(thread.lastMessage);
    setShowInboxDetail(true);
    
    // Mark all messages in thread as read
    thread.messages.forEach(msg => {
      if (!msg.read && msg.sender.id !== user?.id) {
        markMessageAsRead(msg.id);
      }
    });
  };

  const handleSelectTeamThread = (thread) => {
    setSelectedTeamThread(thread);
    setShowTeamThreadDetail(true);
    
    // If it's a channel thread, also select the channel
    if (thread.channelId && selectedTeam) {
      const channel = channels.find(c => c.id === thread.channelId);
      if (channel) {
        setSelectedChannel(channel);
      }
    }
    
    // Mark all messages in thread as read
    thread.messages.forEach(msg => {
      if (!msg.read && msg.sender.id !== user?.id) {
        markMessageAsRead(msg.id);
      }
    });
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const messageDate = new Date(timestamp);
    const diff = now - messageDate;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const formatInboxTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatLongTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getInboxSubject = (message) => {
    if (!message) return '';
    if (message.emailSubject) return message.emailSubject;

    if (message.messageType === 'direct') {
      if (message.sender?.id === user?.id && message.recipient?.name) {
        return `Conversation with ${message.recipient.name}`;
      }

      if (message.sender?.name) {
        return `Direct message from ${message.sender.name}`;
      }
    }

    const content = message.content?.trim();
    if (content && content.length > 0) {
      return content.length > 80 ? `${content.slice(0, 77)}...` : content;
    }

    return 'Message';
  };

  const getInboxPreview = (message) => {
    if (!message) return '';

    const content = message.content?.trim();
    if (!content) return '';

    return content.length > 140 ? `${content.slice(0, 137)}...` : content;
  };

  async function markMessageAsRead(messageId) {
    if (!user?.token || !messageId) return;

    try {
      await axios.post(
        `/api/messages/${messageId}/read`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        }
      );

      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, read: true }
            : msg
        )
      );
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }

  // Group team messages into conversation threads (channels, team announcements, etc.)
  const groupTeamMessagesIntoThreads = (messages) => {
    const threadMap = new Map();

    // First, add all channels as threads (even if they have no messages)
    channels.forEach(channel => {
      // Skip if filtering by team and channel doesn't match
      if (selectedTeam && !showAllTeams && channel.teamId !== selectedTeam.id) {
        return;
      }
      
      const threadKey = `channel:${channel.id}`;
      if (!threadMap.has(threadKey)) {
        threadMap.set(threadKey, {
          threadKey,
          threadName: channel.name,
          channelId: channel.id,
          channelName: channel.name,
          messages: [],
          lastMessage: null,
          lastMessageTime: channel.createdAt || new Date().toISOString(),
          unreadCount: 0,
          messageType: 'channel',
          teamId: channel.teamId,
          teamName: channel.teamName,
          participants: [],
          emailSubject: null
        });
      }
    });

    // Then, process messages and add/update threads
    messages.forEach(message => {
      let threadKey;
      let threadName = '';
      let channelId = null;
      let channelName = null;
      const participants = new Map(); // Track all participants for better naming

      if (message.messageType === 'channel' && message.channelId) {
        // Channel messages: group by channel - use channel name as label
        threadKey = `channel:${message.channelId}`;
        // Get channel name from channels state if available, otherwise use message data
        const channel = channels.find(c => c.id === message.channelId);
        threadName = channel?.name || message.channelName || 'Channel';
        channelId = message.channelId;
        channelName = channel?.name || message.channelName;
      } else if (message.messageType === 'team') {
        // Team messages: group by team - use team name as label
        if (selectedTeam && !showAllTeams) {
          threadKey = `team:${message.teamId}:announcements`;
          threadName = selectedTeam.name || 'Team Chat';
        } else {
          threadKey = `team:${message.teamId}`;
          threadName = message.teamName || 'Team Chat';
        }
      } else if (message.messageType === 'direct') {
        // Direct messages: normalize participant order - use recipient name
        const user1Id = message.sender.id;
        const user2Id = message.recipient?.id;
        if (!user2Id) return;
        
        const sortedIds = [user1Id, user2Id].sort((a, b) => a - b);
        threadKey = `direct:${sortedIds[0]}:${sortedIds[1]}`;
        
        // Use the other person's name (not current user)
        if (user1Id === user?.id) {
          threadName = message.recipient?.name || 'Unknown';
          participants.set(user2Id, { id: user2Id, name: message.recipient?.name || 'Unknown' });
        } else {
          threadName = message.sender?.name || 'Unknown';
          participants.set(user1Id, { id: user1Id, name: message.sender?.name || 'Unknown' });
        }
      } else if (message.messageType === 'email') {
        // Email messages: group by participants - create descriptive group name
        const recipientIds = Array.isArray(message.emailRecipients) 
          ? message.emailRecipients.map(Number)
          : [];
        
        const allParticipantIds = [message.sender.id, ...recipientIds]
          .filter(id => id !== user?.id)
          .sort((a, b) => a - b);
        
        threadKey = `email:${allParticipantIds.join(':')}`;
        
        // Build participant map from all messages (we'll update names later)
        participants.set(message.sender.id, { 
          id: message.sender.id, 
          name: message.sender?.name || 'Unknown' 
        });
        recipientIds.forEach(id => {
          if (id !== user?.id && !participants.has(id)) {
            // Try to find recipient name in message data
            participants.set(id, { id, name: 'User' }); // Will be updated from messages
          }
        });
        
        // Try to use email subject if available, otherwise we'll build name list after processing all messages
        if (message.emailSubject) {
          threadName = message.emailSubject;
        } else {
          // Temporary name - will be updated after processing all messages
          threadName = `Group Chat`;
        }
      } else {
        return; // Skip unknown message types
      }

      if (!threadKey) return;

      if (!threadMap.has(threadKey)) {
        threadMap.set(threadKey, {
          threadKey,
          threadName,
          channelId,
          channelName,
          messages: [],
          lastMessage: null,
          lastMessageTime: null,
          unreadCount: 0,
          messageType: message.messageType,
          teamId: message.teamId,
          teamName: message.teamName,
          participants: Array.from(participants.values()),
          emailSubject: message.emailSubject || null
        });
      }

      const thread = threadMap.get(threadKey);
      thread.messages.push(message);
      
      // Update participant names from message data
      if (message.messageType === 'email') {
        // Update sender
        const senderPart = thread.participants.find(p => p.id === message.sender.id);
        if (senderPart && message.sender?.name) {
          senderPart.name = message.sender.name;
        }
        // Update recipients if we have recipient data
        if (message.recipient && !thread.participants.find(p => p.id === message.recipient.id)) {
          thread.participants.push({
            id: message.recipient.id,
            name: message.recipient.name || 'Unknown'
          });
        }
      }
      
      // Update last message if this is newer
      if (!thread.lastMessageTime || new Date(message.timestamp) > new Date(thread.lastMessageTime)) {
        thread.lastMessage = message;
        thread.lastMessageTime = message.timestamp;
      }
      
      // Keep track of email subject if available
      if (message.messageType === 'email' && message.emailSubject && !thread.emailSubject) {
        thread.emailSubject = message.emailSubject;
      }

      // Count unread messages (not sent by current user)
      if (!message.read && message.sender.id !== user?.id) {
        thread.unreadCount++;
      }
    });

    // Post-process threads to improve names - collect all participant info from messages
    threadMap.forEach((thread, key) => {
      if (thread.messageType === 'email') {
        // Build comprehensive participant list from all messages in thread
        // Collect all unique senders and recipients we've seen
        const participantMap = new Map();
        const allRecipientIds = new Set();
        
        // First, collect all recipient IDs
        thread.messages.forEach(msg => {
          if (msg.emailRecipients && Array.isArray(msg.emailRecipients)) {
            msg.emailRecipients.forEach(id => {
              if (id !== user?.id) {
                allRecipientIds.add(id);
              }
            });
          }
        });
        
        // Then, map IDs to names from message senders (best we can do without API call)
        thread.messages.forEach(msg => {
          // Add sender
          if (msg.sender && msg.sender.id !== user?.id) {
            participantMap.set(msg.sender.id, msg.sender.name || 'Unknown');
          }
        });
        
        // For recipient IDs we don't have names for, we'll need to use a placeholder
        // In a real implementation, you might want to fetch user info, but for now
        // we'll use what we have from senders
        allRecipientIds.forEach(id => {
          if (!participantMap.has(id)) {
            // Try to see if this ID appears as a sender in any message
            const foundSender = thread.messages.find(m => m.sender.id === id);
            if (foundSender) {
              participantMap.set(id, foundSender.sender.name || 'Unknown');
            } else {
              participantMap.set(id, null); // Will be filtered out
            }
          }
        });
        
        // Update thread participants (only include ones we have names for)
        thread.participants = Array.from(participantMap.entries())
          .filter(([id, name]) => name && name !== 'User')
          .map(([id, name]) => ({
            id,
            name
          }));
        
        // Build thread name - use subject if available, otherwise build from participants
        if (thread.emailSubject) {
          thread.threadName = thread.emailSubject;
        } else {
          const participantNames = thread.participants
            .filter(p => p.id !== user?.id)
            .map(p => p.name)
            .filter(name => name);
          
          if (participantNames.length === 0) {
            // Fallback: count recipients if we don't have names
            const recipientCount = allRecipientIds.size;
            if (recipientCount === 0) {
              thread.threadName = 'Group Chat';
            } else if (recipientCount === 1) {
              thread.threadName = 'Group Chat';
            } else {
              thread.threadName = `Group Chat (${recipientCount})`;
            }
          } else if (participantNames.length === 1) {
            thread.threadName = participantNames[0];
          } else if (participantNames.length <= 4) {
            // Show up to 4 names: "John, Sarah, Mike, Alex"
            thread.threadName = participantNames.join(', ');
          } else {
            // Show first 2 names + count: "John, Sarah, and 3 others"
            const othersCount = participantNames.length - 2;
            thread.threadName = `${participantNames.slice(0, 2).join(', ')}, and ${othersCount} other${othersCount !== 1 ? 's' : ''}`;
          }
        }
      } else if (thread.messageType === 'team') {
        // Ensure team name is clear - use team name as label
        if (thread.teamName) {
          thread.threadName = thread.teamName;
        }
      } else if (thread.messageType === 'channel') {
        // Channel name should already be set correctly - just ensure it's clean
        if (thread.channelName) {
          thread.threadName = thread.channelName;
        }
      }
    });

    // Convert map to array and sort by last message time (newest first)
    // For threads without messages, use creation time or a default time
    return Array.from(threadMap.values())
      .map(thread => ({
        ...thread,
        messages: thread.messages.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        )
      }))
      .sort((a, b) => {
        const timeA = a.lastMessageTime ? new Date(a.lastMessageTime) : new Date(0);
        const timeB = b.lastMessageTime ? new Date(b.lastMessageTime) : new Date(0);
        return timeB - timeA;
      });
  };

  // Group messages into conversation threads
  const groupMessagesIntoThreads = (messages) => {
    const threadMap = new Map();

    messages.forEach(message => {
      // Only group direct and email messages for inbox threads
      if (message.messageType !== 'direct' && message.messageType !== 'email') {
        return;
      }

      let threadKey;
      let participants = [];
      let threadName = '';

      if (message.messageType === 'direct') {
        // Direct message thread: normalize participant order
        const user1Id = message.sender.id;
        const user2Id = message.recipient?.id;
        if (!user2Id) return;
        
        const sortedIds = [user1Id, user2Id].sort((a, b) => a - b);
        threadKey = `direct:${sortedIds[0]}:${sortedIds[1]}`;
        
        // Participants: the other person (not current user)
        if (user1Id === user?.id) {
          participants = [{ id: user2Id, name: message.recipient?.name || 'Unknown' }];
          threadName = message.recipient?.name || 'Unknown';
        } else {
          participants = [{ id: user1Id, name: message.sender?.name || 'Unknown' }];
          threadName = message.sender?.name || 'Unknown';
        }
      } else if (message.messageType === 'email') {
        // Email thread: group by participants (sender + recipients)
        const recipientIds = Array.isArray(message.emailRecipients) 
          ? message.emailRecipients.map(Number)
          : [];
        
        // Normalize participant IDs (exclude current user from the key but include in display)
        const allParticipantIds = [message.sender.id, ...recipientIds]
          .filter(id => id !== user?.id)
          .sort((a, b) => a - b);
        
        // Create a stable thread key based on all participants
        threadKey = `email:${allParticipantIds.join(':')}`;
        
        // Build participant list (all participants excluding current user)
        const senderObj = { id: message.sender.id, name: message.sender?.name || 'Unknown' };
        const recipientObjs = recipientIds
          .filter(id => id !== user?.id && id !== message.sender.id)
          .map(id => {
            // Try to find recipient info from message data if available
            // Otherwise we'll just use the ID and fetch name later
            return { id, name: `User ${id}` }; // Placeholder, will be improved
          });
        
        participants = [senderObj, ...recipientObjs];
        
        // Thread name: use email subject if available, otherwise build from participants
        if (message.emailSubject) {
          threadName = message.emailSubject;
        } else {
          const participantNames = participants
            .filter(p => p.id !== user?.id)
            .map(p => p.name)
            .filter(name => name && !name.startsWith('User '));
          
          if (participantNames.length === 0) {
            threadName = 'Group Chat';
          } else if (participantNames.length === 1) {
            threadName = participantNames[0];
          } else if (participantNames.length <= 3) {
            threadName = participantNames.join(', ');
          } else {
            const othersCount = participantNames.length - 2;
            threadName = `${participantNames.slice(0, 2).join(', ')}, and ${othersCount} other${othersCount !== 1 ? 's' : ''}`;
          }
        }
      }

      if (!threadKey) return;

      if (!threadMap.has(threadKey)) {
        threadMap.set(threadKey, {
          threadKey,
          threadName,
          participants,
          messages: [],
          lastMessage: null,
          lastMessageTime: null,
          unreadCount: 0,
          messageType: message.messageType,
          emailSubject: message.emailSubject || null
        });
      }

      const thread = threadMap.get(threadKey);
      thread.messages.push(message);
      
      // Update participant names from message data
      if (message.messageType === 'email') {
        // Update sender name
        const senderPart = thread.participants.find(p => p.id === message.sender.id);
        if (senderPart && message.sender?.name) {
          senderPart.name = message.sender.name;
        }
        // Update recipient name if available
        if (message.recipient) {
          const recipientPart = thread.participants.find(p => p.id === message.recipient.id);
          if (recipientPart && message.recipient?.name) {
            recipientPart.name = message.recipient.name;
          }
        }
      }
      
      // Update last message if this is newer
      if (!thread.lastMessageTime || new Date(message.timestamp) > new Date(thread.lastMessageTime)) {
        thread.lastMessage = message;
        thread.lastMessageTime = message.timestamp;
      }
      
      // Keep track of email subject if available
      if (message.messageType === 'email' && message.emailSubject && !thread.emailSubject) {
        thread.emailSubject = message.emailSubject;
        // Update thread name if we now have a subject
        thread.threadName = message.emailSubject;
      }

      // Count unread messages (not sent by current user)
      if (!message.read && message.sender.id !== user?.id) {
        thread.unreadCount++;
      }
    });

    // Post-process inbox threads to improve names
    threadMap.forEach((thread) => {
      if (thread.messageType === 'email' && !thread.emailSubject) {
        // Build better group name from participants
        const participantNames = thread.participants
          .filter(p => p.id !== user?.id)
          .map(p => p.name)
          .filter(name => name && !name.startsWith('User '));
        
        if (participantNames.length === 0) {
          thread.threadName = 'Group Chat';
        } else if (participantNames.length === 1) {
          thread.threadName = participantNames[0];
        } else if (participantNames.length <= 3) {
          thread.threadName = participantNames.join(', ');
        } else {
          const othersCount = participantNames.length - 2;
          thread.threadName = `${participantNames.slice(0, 2).join(', ')}, and ${othersCount} other${othersCount !== 1 ? 's' : ''}`;
        }
      }
    });

    // Convert map to array and sort by last message time (newest first)
    return Array.from(threadMap.values())
      .map(thread => ({
        ...thread,
        messages: thread.messages.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        )
      }))
      .sort((a, b) => 
        new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
      );
  };

  const messageTypeOptions = activeTab === 'team'
    ? [
        { value: 'all', label: 'All Team Messages' },
        { value: 'team', label: 'Team Announcements' },
        { value: 'email', label: 'Email Messages' }
      ]
    : [
        { value: 'all', label: 'All Inbox Items' },
        { value: 'direct', label: 'Direct Messages' },
        { value: 'email', label: 'Email Messages' }
      ];

  const inboxDetailSubject = selectedInboxMessage ? getInboxSubject(selectedInboxMessage) : '';
  const selectedTeamAllowsParentChat = selectedTeam?.settings?.allowParentChat !== false;
  const selectedTeamCanManageChatSettings = canManageTeamChatSettings(selectedTeam);
  const selectedTeamBlockState = getParentCommentBlockState(selectedTeam?.id);
  const selectedTeamThreadTeamId = getThreadTeamId(selectedTeamThread);
  const selectedTeamThreadBlockState = getParentCommentBlockState(selectedTeamThreadTeamId);

  // No team selected state

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className={`h-full flex flex-col ${isMobile ? 'bg-gray-50 text-slate-900 pb-24' : ''}`}>
      {/* Desktop Header - Hidden on Mobile */}
      <div className="hidden md:block bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="space-y-2">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <h1 className="text-2xl font-semibold text-slate-900">Team Communication</h1>
              <div className="inline-flex rounded-full bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => handleTabChange('team')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
                    activeTab === 'team'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Team Chat
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('inbox')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
                    activeTab === 'inbox'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Inbox
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('announcements')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
                    activeTab === 'announcements'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Org News
                </button>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              {activeTab === 'team'
                ? (showAllTeams
                    ? 'Chat messages from all teams'
                    : selectedTeam
                      ? `Chat for ${selectedTeam.name} (${selectedTeam.sport} • ${selectedTeam.ageGroup})`
                      : 'Select a team to view chat messages')
                : activeTab === 'inbox'
                  ? (showAllTeams
                      ? 'Direct messages and email updates from all of your teams'
                      : selectedTeam
                        ? `Inbox for ${selectedTeam.name}`
                        : 'Review your direct messages and email updates in one place')
                  : 'Organization-wide announcements from your admins'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <TeamSelector onTeamChange={handleTeamChange} />

            {activeTab === 'team' && selectedTeam && selectedTeamCanManageChatSettings && (
              <button
                type="button"
                onClick={() => handleUpdateAllowParentChat(!selectedTeamAllowsParentChat)}
                disabled={updatingParentChatSetting}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                title={selectedTeamAllowsParentChat ? 'Disable parent comments' : 'Enable parent comments'}
              >
                <span>Parent comments</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                  selectedTeamAllowsParentChat
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {selectedTeamAllowsParentChat ? 'Allowed' : 'Off'}
                </span>
              </button>
            )}

            {activeTab === 'team' && !showAllTeams && (
              <div className="lg:hidden">
                <select
                  value={selectedChannel?.id || ''}
                  onChange={(e) => {
                    const channel = channels.find(c => c.id === parseInt(e.target.value));
                    if (channel) handleChannelSelect(channel);
                  }}
                  className="w-48 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  {channels
                    .filter(channel => !selectedTeam || channel.teamId === selectedTeam.id)
                    .map(channel => (
                      <option key={channel.id} value={channel.id}>
                        #{channel.name} {channel.isDefault ? '(default)' : ''}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <button
              onClick={handleShowAllTeams}
              className={`rounded-full border px-3 py-2 text-sm font-medium transition ${
                showAllTeams
                  ? 'border-primary-200 bg-primary-100 text-primary-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              Show All Teams
            </button>

            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className={`h-2.5 w-2.5 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-rose-400'}`} />
              <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1">
        {activeTab === 'team' ? (
          <div className={`h-full flex flex-col overflow-hidden rounded-3xl border shadow-md ${
            isMobile
              ? 'border-slate-200 bg-gradient-to-b from-white via-slate-50 to-slate-100'
              : 'border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white'
          }`}>
            <div className="flex flex-1 flex-col lg:flex-row">
              {/* Thread List Sidebar */}
              <div className={`${showTeamThreadDetail ? 'hidden lg:flex' : 'flex'} w-full flex-col border-r transition-all lg:w-96 ${
                isMobile ? 'border-slate-200 bg-white' : 'border-slate-200 bg-white'
              }`}>
                {/* Mobile: Simple Header */}
                <div className={`border-b px-4 py-3 ${isMobile ? 'space-y-3 border-slate-200' : 'space-y-4 border-slate-200'}`}>
                  {isMobile ? (
                    <>
                      <div className="flex items-center justify-between">
                        <h2 className={`text-lg font-semibold ${isMobile ? 'text-slate-900' : 'text-slate-900'}`}>Messages</h2>
                        <button
                          onClick={handleOpenCreateChannel}
                          className={`rounded-full p-2 transition ${isMobile ? 'text-slate-500 hover:bg-slate-100' : 'text-slate-600 hover:bg-slate-100'}`}
                          title="New conversation"
                        >
                          <PlusIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search conversations…"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className={`w-full rounded-2xl border py-2.5 px-4 text-sm shadow-inner focus:outline-none ${
                            isMobile
                              ? 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-400'
                              : 'border-slate-200 bg-white text-slate-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
                          }`}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="text-lg font-semibold text-slate-900">Conversations</h2>
                        <button
                          onClick={handleOpenCreateChannel}
                          className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                          title="Create new channel"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="relative">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search conversations…"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-inner focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                        />
                      </div>

                      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                        <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1a1 1 0 011-1h2a1 1 0 011 1v18a1 1 0 01-1 1H4a1 1 0 01-1-1V1a1 1 0 011-1h2a1 1 0 011 1v3m0 0h8m-8 0H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2h-2" />
                        </svg>
                        <span className="text-sm font-medium text-slate-600">Message type</span>
                        <select
                          value={messageTypeFilter}
                          onChange={(e) => setMessageTypeFilter(e.target.value)}
                          className="ml-auto w-full max-w-xs rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                        >
                          {messageTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {selectedTeam && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      {selectedTeamCanManageChatSettings ? (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-slate-700">Allow parent comments</span>
                          <button
                            type="button"
                            onClick={() => handleUpdateAllowParentChat(!selectedTeamAllowsParentChat)}
                            disabled={updatingParentChatSetting}
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                              selectedTeamAllowsParentChat
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                          >
                            {selectedTeamAllowsParentChat ? 'On' : 'Off'}
                          </button>
                        </div>
                      ) : (!selectedTeamBlockState.blocked ? (
                        <p className="text-xs text-slate-600">Parent comments are enabled.</p>
                      ) : (
                        <p className="text-xs text-amber-700">This team is coach-updates only for parents.</p>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex h-full items-center justify-center px-6 text-sm text-slate-500">
                      Loading conversations…
                    </div>
                  ) : (() => {
                    const threads = groupTeamMessagesIntoThreads(filteredMessages);
                    return threads.length > 0 ? (
                      threads.map(thread => {
                        const isActive = selectedTeamThread?.threadKey === thread.threadKey;
                        const lastMessage = thread.lastMessage;
                        const preview = lastMessage?.content?.trim() 
                          ? (lastMessage.content.length > 60 
                              ? `${lastMessage.content.slice(0, 57)}...` 
                              : lastMessage.content)
                          : '';

                        return (
                          <div key={thread.threadKey} className={`border-b border-slate-100 last:border-b-0 ${isMobile ? '' : ''}`}>
                            <button
                              type="button"
                              onClick={() => handleSelectTeamThread(thread)}
                              className={`flex w-full items-center gap-3 px-4 py-3 text-left transition ${isMobile ? '' : 'sm:px-6'} ${
                                isActive ? 'bg-slate-50' : 'hover:bg-slate-50'
                              }`}
                            >
                              {/* Thread Icon/Avatar */}
                              <div className={`flex-shrink-0 ${isMobile ? 'h-10 w-10' : 'h-12 w-12'}`}>
                                {thread.messageType === 'channel' ? (
                                  <div className={`flex ${isMobile ? 'h-10 w-10' : 'h-12 w-12'} items-center justify-center rounded-full bg-slate-200 text-slate-600`}>
                                    <span className={`${isMobile ? 'text-base' : 'text-lg'} font-bold`}>#</span>
                                  </div>
                                ) : thread.messageType === 'team' ? (
                                  <div className={`flex ${isMobile ? 'h-10 w-10' : 'h-12 w-12'} items-center justify-center rounded-full bg-primary-100 text-primary-600`}>
                                    <UserGroupIcon className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
                                  </div>
                                ) : (
                                  <div className={`flex ${isMobile ? 'h-10 w-10' : 'h-12 w-12'} items-center justify-center rounded-full text-${isMobile ? 'sm' : 'base'} font-semibold ${
                                    lastMessage?.sender?.role === 'coach'
                                      ? 'bg-blue-600 text-white'
                                      : lastMessage?.sender?.role === 'parent'
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-slate-600 text-white'
                                  }`}>
                                    {thread.threadName.charAt(0)}
                                  </div>
                                )}
                              </div>

                              {/* Thread Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-0.5">
                                  <span className={`${isMobile ? 'text-base' : 'text-sm'} font-semibold truncate ${thread.unreadCount > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                                    {thread.threadName}
                                  </span>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {thread.unreadCount > 0 && (
                                      <span className={`flex ${isMobile ? 'h-5 w-5' : 'h-5 w-5'} items-center justify-center rounded-full bg-primary-600 text-[10px] font-semibold text-white`}>
                                        {thread.unreadCount > 9 ? '9+' : thread.unreadCount}
                                      </span>
                                    )}
                                    <span className={`${isMobile ? 'text-xs' : 'text-xs'} font-normal text-slate-500 whitespace-nowrap`}>
                                      {formatInboxTimestamp(thread.lastMessageTime)}
                                    </span>
                                  </div>
                                </div>
                                {preview && (
                                  <p className={`${isMobile ? 'text-sm' : 'text-xs'} truncate ${thread.unreadCount > 0 ? 'text-slate-600 font-medium' : 'text-slate-500'}`}>
                                    {lastMessage.sender.id === user?.id ? 'You: ' : ''}{preview}
                                  </p>
                                )}
                                {!isMobile && thread.teamName && showAllTeams && (
                                  <p className="text-xs text-slate-400 mt-0.5">{thread.teamName}</p>
                                )}
                              </div>
                            </button>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center px-6 text-center text-sm text-slate-500">
                        {searchQuery
                          ? 'No conversations match your filters.'
                          : 'No conversations yet. Start chatting!'}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Thread Detail View */}
              <div className={`${showTeamThreadDetail ? 'flex' : 'hidden lg:flex'} flex-1 flex-col ${isMobile ? 'bg-white' : 'bg-white'}`}>
                {selectedTeamThread ? (
                  <div className="relative flex flex-1 flex-col">
                    {/* Thread Header */}
                    <div className={`border-b ${isMobile ? 'border-slate-200 bg-white px-4 py-3' : 'border-slate-200 bg-white px-4 py-4 sm:px-6'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowTeamThreadDetail(false);
                              setSelectedTeamThread(null);
                            }}
                            className={`inline-flex items-center gap-2 ${isMobile ? 'text-base text-slate-600 hover:text-slate-900' : 'text-sm text-slate-600 hover:text-slate-900'} font-medium transition lg:hidden`}
                          >
                            <svg className={`${isMobile ? 'h-5 w-5' : 'h-4 w-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            {isMobile && <span className="ml-1">Back</span>}
                          </button>
                          {!isMobile && (
                            <>
                              {selectedTeamThread.messageType === 'channel' ? (
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                                  <span className="text-lg font-bold">#</span>
                                </div>
                              ) : selectedTeamThread.messageType === 'team' ? (
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                                  <UserGroupIcon className="h-5 w-5" />
                                </div>
                              ) : (
                                <div className={`flex h-10 w-10 items-center justify-center rounded-full text-base font-semibold ${
                                  selectedTeamThread.lastMessage?.sender?.role === 'coach'
                                    ? 'bg-blue-600 text-white'
                                    : selectedTeamThread.lastMessage?.sender?.role === 'parent'
                                      ? 'bg-emerald-600 text-white'
                                      : 'bg-slate-600 text-white'
                                }`}>
                                  {selectedTeamThread.threadName.charAt(0)}
                                </div>
                              )}
                            </>
                          )}
                          <div>
                            <h2 className={`${isMobile ? 'text-lg text-slate-900' : 'text-lg text-slate-900'} font-semibold`}>{selectedTeamThread.threadName}</h2>
                            {!isMobile && selectedTeamThread.teamName && showAllTeams && (
                              <p className="text-xs text-slate-500">{selectedTeamThread.teamName}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className={`mobile-chat-thread flex-1 overflow-y-auto ${isMobile ? 'px-3 py-4 pb-32 space-y-3 bg-white' : 'px-4 py-6 sm:px-8 space-y-6'}`}>
                      {selectedTeamThread.messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center text-slate-500">
                          <div className={`flex ${isMobile ? 'h-12 w-12' : 'h-16 w-16'} items-center justify-center rounded-full bg-slate-100 ${isMobile ? 'mb-3' : 'mb-4'}`}>
                            <svg className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} text-slate-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </div>
                          <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-slate-900 ${isMobile ? 'mb-1' : 'mb-2'}`}>No messages yet</h3>
                          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-slate-500`}>
                            {selectedTeamThread.messageType === 'channel' 
                              ? 'Be the first to send a message in this channel!'
                              : 'Start the conversation!'}
                          </p>
                        </div>
                      ) : (
                        selectedTeamThread.messages.map(message => {
                        const isMine = message.sender.id === user?.id;
                        const isGroupChat = selectedTeamThread?.participants?.length > 1 || selectedTeamThread?.messageType === 'team' || selectedTeamThread?.messageType === 'channel' || selectedTeamThread?.messageType === 'email';

                        return (
                          <div
                            key={message.id}
                            className={`flex items-end ${isMine ? 'justify-end' : 'justify-start'} ${isMobile ? 'gap-2' : 'gap-3'}`}
                          >
                            <div className={`flex flex-col ${isMine ? 'items-end' : 'items-start'} ${isMobile ? 'max-w-[85%]' : 'max-w-[70%]'}`}>
                              {(isGroupChat || !isMine) && (
                                <span className={`${isMobile ? 'text-xs text-slate-600' : 'text-xs text-slate-600'} font-medium ${isMobile ? 'mb-0.5 px-1.5' : 'mb-1 px-1'}`}>
                                  {isMine ? 'You' : (message.sender?.name || 'Unknown')}
                                </span>
                              )}
                              <div className="relative group">
                                <div className={`${isMobile ? 'rounded-2xl px-3 py-2' : 'rounded-2xl px-4 py-2.5'} ${isMobile ? 'text-base' : 'text-sm'} ${
                                  isMine 
                                    ? isMobile 
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-primary-600 text-white'
                                    : isMobile
                                      ? 'bg-slate-100 text-slate-900'
                                      : 'bg-slate-100 text-slate-800'
                                }`}>
                                  <p className="whitespace-pre-line break-words">{message.content}</p>
                                  {message.attachments && message.attachments.length > 0 && (
                                    <div className="mt-2 space-y-2">
                                      {message.attachments.map((attachment, idx) => (
                                        attachment.type === 'image' && (
                                          <img
                                            key={idx}
                                            src={attachment.url}
                                            alt={attachment.filename || 'Image'}
                                            className="max-w-full h-auto rounded-lg border border-gray-200"
                                            style={{ maxHeight: '400px' }}
                                          />
                                        )
                                      ))}
                                    </div>
                                  )}
                                  {message.messageType === 'email' && message.emailSubject && (
                                    <div className={`mt-2 rounded-lg border px-2 py-1.5 text-xs ${
                                      isMine
                                        ? 'border-primary-400/60 bg-primary-500/20 text-white'
                                        : 'border-blue-200 bg-blue-50 text-blue-700'
                                    }`}>
                                      <div className="font-medium">{message.emailSubject}</div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Reaction picker button - shows on hover (desktop) or always visible (mobile) */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowReactionPicker(showReactionPicker === message.id ? null : message.id);
                                  }}
                                  className={`absolute ${isMine ? 'left-0 -translate-x-8' : 'right-0 translate-x-8'} top-1/2 -translate-y-1/2 ${isMobile ? 'opacity-30' : 'opacity-0 group-hover:opacity-100'} transition-opacity ${isMobile ? 'p-1' : 'p-1.5'} rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 active:bg-slate-100`}
                                  title="Add reaction"
                                >
                                  <FaceSmileIcon className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-slate-600`} />
                                </button>
                                
                                {/* Reaction picker popover */}
                                {showReactionPicker === message.id && (
                                  <div className={`absolute ${isMine ? 'right-0' : 'left-0'} top-full mt-1 z-10 bg-white rounded-lg shadow-lg border border-slate-200 ${isMobile ? 'p-1.5' : 'p-2'} flex gap-1`}>
                                    {commonEmojis.map(emoji => (
                                      <button
                                        key={emoji}
                                        onClick={() => handleReaction(message.id, emoji)}
                                        className={`${isMobile ? 'p-2 text-xl' : 'p-1.5 text-lg'} rounded hover:bg-slate-100 transition active:bg-slate-200`}
                                        title={`React with ${emoji}`}
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              {/* Display reactions */}
                              {message.reactions && Object.keys(message.reactions).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1 px-1">
                                  {Object.entries(message.reactions).map(([emoji, userIds]) => {
                                    if (!userIds || userIds.length === 0) return null;
                                    const hasReacted = userIds.includes(user?.id);
                                    return (
                                      <button
                                        key={emoji}
                                        onClick={() => handleReaction(message.id, emoji)}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition ${
                                          hasReacted
                                            ? 'bg-primary-100 border border-primary-300 text-primary-700'
                                            : 'bg-slate-100 border border-slate-200 text-slate-700 hover:bg-slate-200'
                                        }`}
                                        title={hasReacted ? 'Remove reaction' : 'Add reaction'}
                                      >
                                        <span>{emoji}</span>
                                        <span className="font-medium">{userIds.length}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              
                              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-slate-400 ${isMobile ? 'px-1.5 mt-0.5' : 'px-1'}`}>
                                {formatTimestamp(message.timestamp)}
                              </span>
                            </div>
                          </div>
                        );
                      }))}
                    </div>

                    {/* Reply Input */}
                    <div className={`mobile-chat-composer ${isMobile ? 'mobile-chat-composer-overlay border-transparent bg-white px-3 py-2.5 pb-[calc(env(safe-area-inset-bottom,0px)+8px)]' : 'border-t border-slate-200 bg-white px-4 py-4 sm:px-6'} safe-area-inset-bottom`}>
                      {selectedTeamThreadBlockState.blocked && (
                        <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                          {selectedTeamThreadBlockState.reason}
                        </div>
                      )}
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        if ((!newMessage.trim() && pendingAttachments.length === 0) || !selectedTeamThread) return;

                        try {
                          let messageData;
                          
                          // Get teamId from thread, channel, or message
                          let threadTeamId = selectedTeamThread.teamId;
                          if (!threadTeamId && selectedTeamThread.channelId) {
                            // For channels, get teamId from the channel data
                            const channel = channels.find(c => c.id === selectedTeamThread.channelId);
                            if (channel) {
                              threadTeamId = channel.teamId;
                            }
                          }
                          if (!threadTeamId) {
                            threadTeamId = selectedTeamThread.messages[0]?.teamId;
                          }
                          
                          if (!threadTeamId) {
                            toast.error('Unable to determine team for this conversation');
                            return;
                          }

                          if (['team', 'channel'].includes(selectedTeamThread.messageType)) {
                            const blockState = getParentCommentBlockState(threadTeamId);
                            if (blockState.blocked) {
                              toast.error(blockState.reason);
                              return;
                            }
                          }
                          
                          if (selectedTeamThread.messageType === 'channel') {
                            messageData = {
                              content: newMessage.trim() || '',
                              teamId: threadTeamId,
                              channelId: selectedTeamThread.channelId,
                              messageType: 'channel',
                              tags: [],
                              attachments: pendingAttachments
                            };
                          } else if (selectedTeamThread.messageType === 'team') {
                            messageData = {
                              content: newMessage.trim() || '',
                              teamId: threadTeamId,
                              messageType: 'team',
                              tags: [],
                              attachments: pendingAttachments
                            };
                          } else if (selectedTeamThread.messageType === 'direct') {
                            const recipientIds = selectedTeamThread.messages
                              .map(m => m.sender.id === user?.id ? m.recipient?.id : m.sender.id)
                              .filter(Boolean);
                            messageData = {
                              content: newMessage.trim() || '',
                              teamId: threadTeamId,
                              messageType: 'direct',
                              recipientId: recipientIds[0],
                              tags: [],
                              attachments: pendingAttachments
                            };
                          } else {
                            return;
                          }

                          const response = await axios.post('/api/messages', messageData, {
                            headers: {
                              'Authorization': `Bearer ${user.token}`
                            }
                          });

                          const newMessagePayload = {
                            ...response.data,
                            tags: Array.isArray(response.data.tags) ? response.data.tags : [],
                            read: response.data.read !== undefined ? response.data.read : false
                          };
                          
                          // Add message to state
                          setMessages(prev => {
                            // Avoid duplicates if message already exists
                            const exists = prev.find(m => m.id === newMessagePayload.id);
                            if (exists) return prev;
                            return [...prev, newMessagePayload];
                          });
                          
                          if (isConnected) {
                            emit('send_message', response.data);
                          }

                          setNewMessage('');
                          setPendingAttachments([]);
                          toast.success('Message sent successfully!');
                          
                          // Refresh messages after a short delay to ensure consistency
                          // but don't block the UI update
                          setTimeout(() => {
                            fetchMessages();
                          }, 500);
                        } catch (error) {
                          console.error('Error sending message:', error);
                          toast.error('Failed to send message: ' + (error.response?.data?.message || error.message));
                        }
                      }} className={`relative flex ${isMobile ? 'items-center rounded-[28px] border border-slate-200 bg-white px-2 py-2 backdrop-blur-md' : 'gap-3 items-end'}`}>
                        {pendingAttachments.length > 0 && (
                          <div className="absolute bottom-full mb-2 left-0 flex flex-wrap gap-2">
                            {pendingAttachments.map((attachment, idx) => (
                              <div key={idx} className="relative">
                                <img
                                  src={attachment.url}
                                  alt={attachment.filename || 'Image'}
                                  className="h-20 w-20 rounded border border-gray-200 object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {isMobile && (
                          <button
                            type="button"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600"
                            aria-label="Add attachment"
                          >
                            <PlusIcon className="h-5 w-5" />
                          </button>
                        )}
                        <input
                          type="text"
                          placeholder={isMobile ? "Message" : "Type a message..."}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onPaste={handlePasteImage}
                          disabled={selectedTeamThreadBlockState.blocked}
                          className={`flex-1 ${isMobile ? 'rounded-3xl border-0 bg-transparent px-2 py-2.5 text-base text-slate-900 placeholder:text-slate-400' : 'rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-200'} focus:border-primary-500 focus:outline-none`}
                        />
                        {isMobile && (
                          <button
                            type="button"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500"
                            aria-label="Emoji"
                          >
                            <FaceSmileIcon className="h-6 w-6" />
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={selectedTeamThreadBlockState.blocked || (!newMessage.trim() && pendingAttachments.length === 0)}
                          className={`inline-flex items-center justify-center ${isMobile ? 'h-10 w-10 rounded-full bg-blue-500 p-0 hover:bg-blue-600' : 'rounded-xl bg-primary-600 px-4 py-2.5 hover:bg-primary-700 shadow-sm focus:ring-2 focus:ring-primary-400'} text-white transition focus:outline-none disabled:cursor-not-allowed disabled:opacity-60`}
                        >
                          <PaperAirplaneIcon className={`${isMobile ? 'h-5 w-5' : 'h-5 w-5'}`} />
                        </button>
                      </form>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center bg-white px-6 text-center text-slate-500">
                    <h3 className="text-lg font-semibold text-slate-900">Select a conversation</h3>
                    <p className="mt-2 text-sm">
                      Choose a conversation from the list to see messages.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeTab === 'inbox' ? (
          <div className={`h-full flex flex-col overflow-hidden rounded-3xl border shadow-md ${
            isMobile
              ? 'border-slate-200 bg-gradient-to-b from-white via-slate-50 to-slate-100'
              : 'border-slate-200 bg-gradient-to-br from-indigo-50 via-white to-white'
          }`}>
            <div className="flex flex-1 flex-col lg:flex-row">
              <div className={`${showInboxDetail ? 'hidden lg:flex' : 'flex'} w-full flex-col border-r transition-all lg:w-96 ${
                isMobile ? 'border-slate-200 bg-white' : 'border-slate-200 bg-white'
              }`}>
                {/* Mobile: Simple Header */}
                <div className={`border-b ${isMobile ? 'space-y-3 px-4 py-3 border-slate-200' : 'space-y-4 px-4 py-5 sm:px-6 border-slate-200'}`}>
                  {isMobile ? (
                    <>
                      <div className="flex items-center justify-between">
                        <h2 className={`text-lg font-semibold ${isMobile ? 'text-slate-900' : 'text-slate-900'}`}>Messages</h2>
                        <button
                          onClick={handleOpenCompose}
                          disabled={!selectedTeam}
                          className="rounded-full p-2 text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
                          title="New message"
                        >
                          <PlusIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search inbox…"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className={`w-full rounded-2xl border py-2.5 px-4 text-sm shadow-inner focus:outline-none ${
                            isMobile
                              ? 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-400'
                              : 'border-slate-200 bg-white text-slate-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
                          }`}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="text-lg font-semibold text-slate-900">Conversations</h2>
                        <button
                          onClick={handleOpenCompose}
                          disabled={!selectedTeam}
                          className="flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <PlusIcon className="h-4 w-4" />
                          <span className="hidden sm:inline">New</span>
                        </button>
                      </div>

                      <div className="relative">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search inbox…"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-inner focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                        />
                      </div>

                      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                        <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1a1 1 0 011-1h2a1 1 0 011 1v18a1 1 0 01-1 1H4a1 1 0 01-1-1V1a1 1 0 011-1h2a1 1 0 011 1v3m0 0h8m-8 0H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2h-2" />
                        </svg>
                        <span className="text-sm font-medium text-slate-600">Item type</span>
                        <select
                          value={messageTypeFilter}
                          onChange={(e) => setMessageTypeFilter(e.target.value)}
                          className="ml-auto w-full max-w-xs rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                        >
                          {messageTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex h-full items-center justify-center px-6 text-sm text-slate-500">
                      Loading inbox…
                    </div>
                  ) : (() => {
                    const threads = groupMessagesIntoThreads(filteredMessages);
                    return threads.length > 0 ? (
                      threads.map(thread => {
                        const isActive = selectedThread?.threadKey === thread.threadKey;
                        const lastMessage = thread.lastMessage;
                        const preview = lastMessage?.content?.trim() 
                          ? (lastMessage.content.length > 60 
                              ? `${lastMessage.content.slice(0, 57)}...` 
                              : lastMessage.content)
                          : '';

                        return (
                          <div key={thread.threadKey} className="border-b border-slate-100 last:border-b-0">
                            <button
                              type="button"
                              onClick={() => handleSelectThread(thread)}
                              className={`flex w-full items-center ${isMobile ? 'gap-3 px-4 py-3' : 'gap-3 px-4 py-4 sm:px-6'} text-left transition ${
                                isActive ? 'bg-slate-50' : 'hover:bg-slate-50'
                              }`}
                            >
                              {/* Avatar */}
                              <div className={`flex-shrink-0 ${isMobile ? 'h-10 w-10' : 'h-12 w-12'}`}>
                                {thread.participants.length === 1 ? (
                                  <div className={`flex ${isMobile ? 'h-10 w-10' : 'h-12 w-12'} items-center justify-center rounded-full ${isMobile ? 'text-sm' : 'text-base'} font-semibold ${
                                    lastMessage?.sender?.role === 'coach'
                                      ? 'bg-blue-600 text-white'
                                      : lastMessage?.sender?.role === 'parent'
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-slate-600 text-white'
                                  }`}>
                                    {thread.participants[0]?.name?.charAt(0) || '?'}
                                  </div>
                                ) : (
                                  <div className={`relative ${isMobile ? 'h-10 w-10' : 'h-12 w-12'}`}>
                                    <div className={`absolute bottom-0 right-0 flex ${isMobile ? 'h-7 w-7' : 'h-8 w-8'} items-center justify-center rounded-full bg-slate-600 text-white ${isMobile ? 'text-[10px]' : 'text-xs'} font-semibold`}>
                                      {thread.participants.length}
                                    </div>
                                    <div className={`absolute top-0 left-0 flex ${isMobile ? 'h-7 w-7' : 'h-8 w-8'} items-center justify-center rounded-full bg-blue-600 text-white ${isMobile ? 'text-[10px]' : 'text-xs'} font-semibold`}>
                                      {thread.participants[0]?.name?.charAt(0) || '?'}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Thread Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-0.5">
                                  <span className={`${isMobile ? 'text-base' : 'text-sm'} font-semibold truncate ${thread.unreadCount > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                                    {thread.threadName}
                                  </span>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {thread.unreadCount > 0 && (
                                      <span className={`flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[10px] font-semibold text-white`}>
                                        {thread.unreadCount > 9 ? '9+' : thread.unreadCount}
                                      </span>
                                    )}
                                    <span className={`${isMobile ? 'text-xs' : 'text-xs'} font-normal text-slate-500 whitespace-nowrap`}>
                                      {formatInboxTimestamp(thread.lastMessageTime)}
                                    </span>
                                  </div>
                                </div>
                                {preview && (
                                  <p className={`${isMobile ? 'text-sm' : 'text-xs'} truncate ${thread.unreadCount > 0 ? 'text-slate-600 font-medium' : 'text-slate-500'}`}>
                                    {lastMessage.sender.id === user?.id ? 'You: ' : ''}{preview}
                                  </p>
                                )}
                              </div>
                            </button>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center px-6 text-center text-sm text-slate-500">
                        {searchQuery
                          ? 'No conversations match your filters.'
                          : 'Your inbox is clear. Messages will appear here when your team reaches out.'}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className={`${showInboxDetail ? 'flex' : 'hidden lg:flex'} flex-1 flex-col ${isMobile ? 'bg-white' : 'bg-white'}`}>
                {selectedThread ? (
                  <div className="relative flex flex-1 flex-col">
                    {/* Thread Header */}
                    <div className={`border-b ${isMobile ? 'border-slate-200 bg-white px-4 py-3' : 'border-slate-200 bg-white px-4 py-4 sm:px-6'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowInboxDetail(false);
                              setSelectedThread(null);
                              setSelectedInboxMessage(null);
                            }}
                            className={`inline-flex items-center gap-2 ${isMobile ? 'text-base text-slate-600 hover:text-slate-900' : 'text-sm text-slate-600 hover:text-slate-900'} font-medium transition lg:hidden`}
                          >
                            <svg className={`${isMobile ? 'h-5 w-5' : 'h-4 w-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            {isMobile && <span className="ml-1">Back</span>}
                          </button>
                          {!isMobile && (
                            <>
                              {selectedThread.participants.length === 1 ? (
                                <div className={`flex h-10 w-10 items-center justify-center rounded-full text-base font-semibold ${
                                  selectedThread.lastMessage?.sender?.role === 'coach'
                                    ? 'bg-blue-600 text-white'
                                    : selectedThread.lastMessage?.sender?.role === 'parent'
                                      ? 'bg-emerald-600 text-white'
                                      : 'bg-slate-600 text-white'
                                }`}>
                                  {selectedThread.participants[0]?.name?.charAt(0) || '?'}
                                </div>
                              ) : (
                                <div className="relative h-10 w-10">
                                  <div className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-slate-600 text-white text-xs font-semibold">
                                    {selectedThread.participants.length}
                                  </div>
                                  <div className="absolute top-0 left-0 flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-white text-xs font-semibold">
                                    {selectedThread.participants[0]?.name?.charAt(0) || '?'}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          <div>
                            <h2 className={`${isMobile ? 'text-lg text-slate-900' : 'text-lg text-slate-900'} font-semibold`}>{selectedThread.threadName}</h2>
                            {!isMobile && selectedThread.participants.length > 1 && (
                              <p className="text-xs text-slate-500">
                                {selectedThread.participants.length} participants
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className={`mobile-chat-thread flex-1 overflow-y-auto ${isMobile ? 'px-3 py-4 pb-32 space-y-3 bg-white' : 'px-4 py-6 sm:px-8 space-y-6'}`}>
                      {selectedThread.messages.map(message => {
                        const isMine = message.sender.id === user?.id;
                        const isGroupChat = selectedThread?.participants?.length > 1 || selectedThread?.messageType === 'team' || selectedThread?.messageType === 'channel' || selectedThread?.messageType === 'email';

                        return (
                          <div
                            key={message.id}
                            className={`flex items-end ${isMine ? 'justify-end' : 'justify-start'} ${isMobile ? 'gap-2' : 'gap-3'}`}
                          >
                            <div className={`flex flex-col ${isMine ? 'items-end' : 'items-start'} ${isMobile ? 'max-w-[85%]' : 'max-w-[70%]'}`}>
                              {(isGroupChat || !isMine) && (
                                <span className={`${isMobile ? 'text-xs text-slate-600' : 'text-xs text-slate-600'} font-medium ${isMobile ? 'mb-0.5 px-1.5' : 'mb-1 px-1'}`}>
                                  {isMine ? 'You' : (message.sender?.name || 'Unknown')}
                                </span>
                              )}
                              <div className="relative group">
                                <div className={`${isMobile ? 'rounded-2xl px-3 py-2' : 'rounded-2xl px-4 py-2.5'} ${isMobile ? 'text-base' : 'text-sm'} ${
                                  isMine 
                                    ? isMobile
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-primary-600 text-white'
                                    : isMobile
                                      ? 'bg-slate-100 text-slate-900'
                                      : 'bg-slate-100 text-slate-800'
                                }`}>
                                  <p className="whitespace-pre-line break-words">{message.content}</p>
                                  {message.attachments && message.attachments.length > 0 && (
                                    <div className="mt-2 space-y-2">
                                      {message.attachments.map((attachment, idx) => (
                                        attachment.type === 'image' && (
                                          <img
                                            key={idx}
                                            src={attachment.url}
                                            alt={attachment.filename || 'Image'}
                                            className="max-w-full h-auto rounded-lg border border-gray-200"
                                            style={{ maxHeight: '400px' }}
                                          />
                                        )
                                      ))}
                                    </div>
                                  )}
                                  {message.messageType === 'email' && message.emailSubject && (
                                    <div className={`mt-2 rounded-lg border px-2 py-1.5 text-xs ${
                                      isMine
                                        ? 'border-primary-400/60 bg-primary-500/20 text-white'
                                        : 'border-blue-200 bg-blue-50 text-blue-700'
                                    }`}>
                                      <div className="font-medium">{message.emailSubject}</div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Reaction picker button - shows on hover (desktop) or always visible (mobile) */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowReactionPicker(showReactionPicker === message.id ? null : message.id);
                                  }}
                                  className={`absolute ${isMine ? 'left-0 -translate-x-8' : 'right-0 translate-x-8'} top-1/2 -translate-y-1/2 ${isMobile ? 'opacity-30' : 'opacity-0 group-hover:opacity-100'} transition-opacity ${isMobile ? 'p-1' : 'p-1.5'} rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 active:bg-slate-100`}
                                  title="Add reaction"
                                >
                                  <FaceSmileIcon className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-slate-600`} />
                                </button>
                                
                                {/* Reaction picker popover */}
                                {showReactionPicker === message.id && (
                                  <div className={`absolute ${isMobile ? 'right-0' : 'left-0'} top-full mt-1 z-10 bg-white rounded-lg shadow-lg border border-slate-200 ${isMobile ? 'p-1.5' : 'p-2'} flex gap-1`}>
                                    {commonEmojis.map(emoji => (
                                      <button
                                        key={emoji}
                                        onClick={() => handleReaction(message.id, emoji)}
                                        className={`${isMobile ? 'p-2 text-xl' : 'p-1.5 text-lg'} rounded hover:bg-slate-100 transition active:bg-slate-200`}
                                        title={`React with ${emoji}`}
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              {/* Display reactions */}
                              {message.reactions && Object.keys(message.reactions).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1 px-1">
                                  {Object.entries(message.reactions).map(([emoji, userIds]) => {
                                    if (!userIds || userIds.length === 0) return null;
                                    const hasReacted = userIds.includes(user?.id);
                                    return (
                                      <button
                                        key={emoji}
                                        onClick={() => handleReaction(message.id, emoji)}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition ${
                                          hasReacted
                                            ? 'bg-primary-100 border border-primary-300 text-primary-700'
                                            : 'bg-slate-100 border border-slate-200 text-slate-700 hover:bg-slate-200'
                                        }`}
                                        title={hasReacted ? 'Remove reaction' : 'Add reaction'}
                                      >
                                        <span>{emoji}</span>
                                        <span className="font-medium">{userIds.length}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              
                              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-slate-400 ${isMobile ? 'px-1.5 mt-0.5' : 'px-1'}`}>
                                {formatTimestamp(message.timestamp)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Reply Input */}
                    <div className={`mobile-chat-composer ${isMobile ? 'mobile-chat-composer-overlay border-transparent bg-white px-3 py-2.5 pb-[calc(env(safe-area-inset-bottom,0px)+8px)]' : 'border-t border-slate-200 bg-white px-4 py-4 sm:px-6'} safe-area-inset-bottom`}>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        if ((!newMessage.trim() && pendingAttachments.length === 0) || !selectedThread) return;

                        try {
                          const recipientIds = selectedThread.participants.map(p => p.id);
                          // If thread has only one participant (direct message), use direct type
                          // Otherwise, use email type for group messages
                          const messageType = recipientIds.length === 1 ? 'direct' : 'email';
                          const lastMsg = selectedThread.lastMessage;
                          
                          // Get teamId from thread or message
                          const threadTeamId = selectedThread.teamId || selectedThread.messages[0]?.teamId;
                          
                          if (!threadTeamId) {
                            toast.error('Unable to determine team for this conversation');
                            return;
                          }
                          
                          const messageData = {
                            content: newMessage.trim() || '',
                            teamId: threadTeamId,
                            messageType: messageType,
                            recipientId: messageType === 'direct' ? recipientIds[0] : null,
                            emailRecipients: messageType === 'email' ? recipientIds : [],
                            emailSubject: messageType === 'email' && lastMsg?.emailSubject 
                              ? lastMsg.emailSubject 
                              : (messageType === 'email' ? 'Team Message' : null),
                            tags: [],
                            attachments: pendingAttachments
                          };

                          const response = await axios.post('/api/messages', messageData, {
                            headers: {
                              'Authorization': `Bearer ${user.token}`
                            }
                          });

                          const newMessagePayload = {
                            ...response.data,
                            tags: Array.isArray(response.data.tags) ? response.data.tags : [],
                            read: response.data.read !== undefined ? response.data.read : false
                          };
                          
                          // Add message to state
                          setMessages(prev => {
                            // Avoid duplicates if message already exists
                            const exists = prev.find(m => m.id === newMessagePayload.id);
                            if (exists) return prev;
                            return [...prev, newMessagePayload];
                          });
                          
                          if (isConnected) {
                            emit('send_message', response.data);
                          }

                          setNewMessage('');
                          toast.success('Message sent successfully!');
                          
                          // Refresh messages after a short delay to ensure consistency
                          // but don't block the UI update
                          setTimeout(() => {
                            fetchMessages();
                          }, 500);
                        } catch (error) {
                          console.error('Error sending message:', error);
                          toast.error('Failed to send message: ' + (error.response?.data?.message || error.message));
                        }
                      }} className={`relative flex ${isMobile ? 'items-center rounded-[28px] border border-slate-200 bg-white px-2 py-2 backdrop-blur-md' : 'gap-3 items-end'}`}>
                        {isMobile && (
                          <button
                            type="button"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600"
                            aria-label="Add attachment"
                          >
                            <PlusIcon className="h-5 w-5" />
                          </button>
                        )}
                        <input
                          type="text"
                          placeholder={isMobile ? "Message" : "Type a message..."}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onPaste={handlePasteImage}
                          className={`flex-1 ${isMobile ? 'rounded-3xl border-0 bg-transparent px-2 py-2.5 text-base text-slate-900 placeholder:text-slate-400' : 'rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-200'} focus:border-primary-500 focus:outline-none`}
                        />
                        {isMobile && (
                          <button
                            type="button"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500"
                            aria-label="Emoji"
                          >
                            <FaceSmileIcon className="h-6 w-6" />
                          </button>
                        )}
                        {pendingAttachments.length > 0 && (
                          <div className="absolute bottom-full mb-2 left-0 flex flex-wrap gap-2">
                            {pendingAttachments.map((attachment, idx) => (
                              <div key={idx} className="relative">
                                <img
                                  src={attachment.url}
                                  alt={attachment.filename || 'Image'}
                                  className="h-20 w-20 rounded border border-gray-200 object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          type="submit"
                          disabled={!newMessage.trim() && pendingAttachments.length === 0}
                          className={`inline-flex items-center justify-center ${isMobile ? 'h-10 w-10 rounded-full bg-blue-500 p-0 hover:bg-blue-600' : 'rounded-xl bg-primary-600 px-4 py-2.5 hover:bg-primary-700 shadow-sm focus:ring-2 focus:ring-primary-400'} text-white transition focus:outline-none disabled:cursor-not-allowed disabled:opacity-60`}
                        >
                          <PaperAirplaneIcon className={`${isMobile ? 'h-5 w-5' : 'h-5 w-5'}`} />
                        </button>
                      </form>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center bg-white px-6 text-center text-slate-500">
                    <h3 className="text-lg font-semibold text-slate-900">Select a conversation</h3>
                    <p className="mt-2 text-sm">
                      Choose a conversation from your inbox to see messages, or click "New" to start a conversation.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Org Announcements Tab */
          (() => {
            const selectedOrgUser = userOrgs.find(o => o.organization_id === selectedAnnouncementOrgId);
            const orgRole = selectedOrgUser?.role;
            const canPost = ['owner', 'admin'].includes(orgRole);
            const selectedOrgName = selectedOrgUser?.Organization?.name;

            return (
              <div className={`h-full flex flex-col overflow-hidden rounded-3xl border shadow-md ${
                isMobile
                  ? 'border-slate-200 bg-gradient-to-b from-white via-slate-50 to-slate-100'
                  : 'border-slate-200 bg-gradient-to-br from-amber-50 via-white to-white'
              }`}>
                {/* Org selector header */}
                <div className={`flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 ${isMobile ? '' : 'sm:px-6'}`}>
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">Org News</span>
                      {canPost && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                          {orgRole === 'owner' ? 'Owner' : 'Admin'}
                        </span>
                      )}
                    </div>
                    {userOrgs.length > 1 ? (
                      <select
                        value={selectedAnnouncementOrgId || ''}
                        onChange={(e) => setSelectedAnnouncementOrgId(parseInt(e.target.value))}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white py-1 px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
                      >
                        {userOrgs.map(ou => (
                          <option key={ou.organization_id} value={ou.organization_id}>
                            {ou.Organization?.name || `Org ${ou.organization_id}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-slate-500 truncate">{selectedOrgName || 'Loading…'}</p>
                    )}
                  </div>
                </div>

                {/* Messages area */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                  {announcementLoading ? (
                    <div className="flex h-full items-center justify-center text-sm text-slate-500">
                      Loading announcements…
                    </div>
                  ) : announcementMessages.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center text-center text-slate-500 px-6">
                      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-slate-700">No announcements yet</p>
                      {canPost ? (
                        <p className="mt-1 text-xs text-slate-500">Use the box below to post the first announcement.</p>
                      ) : (
                        <p className="mt-1 text-xs text-slate-500">Check back later for updates from your organization admins.</p>
                      )}
                    </div>
                  ) : (
                    announcementMessages.map(announcement => (
                      <div key={announcement.id} className="flex gap-3">
                        <div className="flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-full bg-amber-600 text-white text-sm font-semibold">
                          {announcement.author?.name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-sm font-semibold text-slate-900">{announcement.author?.name || 'Admin'}</span>
                            <span className="text-xs text-slate-400">{formatLongTimestamp(announcement.createdAt)}</span>
                          </div>
                          <div className="rounded-2xl rounded-tl-sm bg-amber-50 border border-amber-100 px-4 py-2.5 text-sm text-slate-800 whitespace-pre-line break-words">
                            {announcement.content}
                          </div>
                          {canPost && (
                            <button
                              onClick={() => handleDeleteAnnouncement(announcement.id)}
                              className="mt-1 text-xs text-slate-400 hover:text-red-500 transition"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={announcementsEndRef} />
                </div>

                {/* Compose box - admins/owners only */}
                {canPost ? (
                  <div className={`border-t border-slate-200 bg-white px-4 py-3 ${isMobile ? '' : 'sm:px-6'}`}>
                    <form onSubmit={handleSendAnnouncement} className="flex gap-3 items-end">
                      <textarea
                        value={newAnnouncementContent}
                        onChange={(e) => setNewAnnouncementContent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (newAnnouncementContent.trim()) handleSendAnnouncement(e);
                          }
                        }}
                        placeholder="Write an announcement to all org members…"
                        rows={2}
                        className="flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-amber-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
                      />
                      <button
                        type="submit"
                        disabled={!newAnnouncementContent.trim()}
                        className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-amber-500 text-white transition hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Post announcement"
                      >
                        <PaperAirplaneIcon className="h-5 w-5" />
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-center text-xs text-slate-400">
                    Only org admins can post announcements
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* Create Channel Modal */}
      {showCreateChannel && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Channel</h3>
                <button
                  onClick={() => {
                    setShowCreateChannel(false);
                    setNewChannelName('');
                    setNewChannelDescription('');
                    setChannelTeamId(null);
                    setChannelMembers([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateChannel} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team <span className="text-red-500">*</span>
                  </label>
                  {fetchingChannelTeams ? (
                    <div className="text-sm text-gray-500">Loading teams...</div>
                  ) : (
                    <select
                      value={channelTeamId || ''}
                      onChange={(e) => handleChannelTeamChange(parseInt(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                      <option value="">Select a team...</option>
                      {availableChannelTeams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} {team.sport ? `(${team.sport})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Channel Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newChannelName}
                    onChange={(e) => setNewChannelName(e.target.value)}
                    placeholder="e.g., Game Strategy, Practice Updates"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={newChannelDescription}
                    onChange={(e) => setNewChannelDescription(e.target.value)}
                    rows={3}
                    placeholder="Describe what this channel is for..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {channelTeamId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Members (optional - leave empty for public channel)
                      {channelMembers.length > 0 && ` (${channelMembers.length} selected)`}
                    </label>
                    {channelMembers.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {channelMembers.map(member => (
                          <span
                            key={member.id}
                            className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700"
                          >
                            {member.name}
                            <button
                              type="button"
                              onClick={() => toggleChannelMember(member)}
                              className="text-primary-500 hover:text-primary-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {fetchingChannelMembers ? (
                      <div className="text-sm text-gray-500">Loading team members...</div>
                    ) : (
                      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                        {availableChannelTeamMembers.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            No team members available
                          </div>
                        ) : (
                          availableChannelTeamMembers.map(member => {
                            const isSelected = channelMembers.find(m => m.id === member.id);
                            return (
                              <button
                                key={member.id}
                                type="button"
                                onClick={() => toggleChannelMember(member)}
                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center gap-3 ${
                                  isSelected ? 'bg-primary-50' : ''
                                }`}
                              >
                                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                                  member.role === 'coach'
                                    ? 'bg-blue-600 text-white'
                                    : member.role === 'parent'
                                      ? 'bg-emerald-600 text-white'
                                      : 'bg-slate-600 text-white'
                                }`}>
                                  {member.name.charAt(0)}
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">{member.name}</div>
                                  <div className="text-xs text-gray-500">{member.role}</div>
                                </div>
                                {isSelected && (
                                  <svg className="h-5 w-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                    {channelMembers.length > 0 && (
                      <p className="mt-2 text-xs text-gray-500">
                        This will be a private channel visible only to selected members.
                      </p>
                    )}
                    {channelMembers.length === 0 && (
                      <p className="mt-2 text-xs text-gray-500">
                        Leave empty to create a public channel visible to all team members.
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateChannel(false);
                      setNewChannelName('');
                      setNewChannelDescription('');
                      setChannelTeamId(null);
                      setChannelMembers([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!newChannelName.trim() || !channelTeamId}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Channel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Compose Message Modal */}
      {showComposeMessage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">New Message</h3>
                <button
                  onClick={() => {
                    setShowComposeMessage(false);
                    setComposeRecipients([]);
                    setComposeSubject('');
                    setNewMessage('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSendComposedMessage} className="space-y-4">
                {!selectedTeam && (
                  <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
                    Please select a team first to send messages.
                  </div>
                )}

                {selectedTeam && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To {composeRecipients.length > 0 && `(${composeRecipients.length} selected)`}
                      </label>
                      {composeRecipients.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {composeRecipients.map(recipient => (
                            <span
                              key={recipient.id}
                              className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700"
                            >
                              {recipient.name}
                              <button
                                type="button"
                                onClick={() => toggleComposeRecipient(recipient)}
                                className="text-primary-500 hover:text-primary-700"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {fetchingMembers ? (
                        <div className="text-sm text-gray-500">Loading team members...</div>
                      ) : (
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                          {availableTeamMembers.map(member => {
                            const isSelected = composeRecipients.find(r => r.id === member.id);
                            return (
                              <button
                                key={member.id}
                                type="button"
                                onClick={() => toggleComposeRecipient(member)}
                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center gap-3 ${
                                  isSelected ? 'bg-primary-50' : ''
                                }`}
                              >
                                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                                  member.role === 'coach'
                                    ? 'bg-blue-600 text-white'
                                    : member.role === 'parent'
                                      ? 'bg-emerald-600 text-white'
                                      : 'bg-slate-600 text-white'
                                }`}>
                                  {member.name.charAt(0)}
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">{member.name}</div>
                                  <div className="text-xs text-gray-500">{member.role}</div>
                                </div>
                                {isSelected && (
                                  <svg className="h-5 w-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {composeRecipients.length > 1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subject (optional)
                        </label>
                        <input
                          type="text"
                          value={composeSubject}
                          onChange={(e) => setComposeSubject(e.target.value)}
                          placeholder="Enter subject..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      {pendingAttachments.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-2">
                          {pendingAttachments.map((attachment, idx) => (
                            <div key={idx} className="relative">
                              <img
                                src={attachment.url}
                                alt={attachment.filename || 'Image'}
                                className="h-20 w-20 rounded border border-gray-200 object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                                className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onPaste={handlePasteImage}
                        placeholder="Type your message..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowComposeMessage(false);
                      setComposeRecipients([]);
                      setComposeSubject('');
                      setNewMessage('');
                      setPendingAttachments([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={(!newMessage.trim() && pendingAttachments.length === 0) || !selectedTeam || composeRecipients.length === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reaction Picker Modal */}
      {showReactionPicker && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-4 border w-auto shadow-lg rounded-md bg-white max-w-sm mx-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Add reaction:</span>
                <button
                  onClick={() => setShowReactionPicker(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {commonEmojis.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(showReactionPicker, emoji)}
                    className="p-3 text-2xl hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamChat;
